import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PlatformEarningsTab from '../components/dashboard/PlatformEarningsTab';
import SystemHealthTab from '../components/dashboard/SystemHealthTab';
import DatabaseTab from '../components/dashboard/DatabaseTab';
import AdminPermissionsModal from '../components/dashboard/AdminPermissionsModal';
import AdminIDVerification from './AdminIDVerification';
import ModerationTab from '../components/moderation/ModerationTab';
import DisputeProgressBadge from '../components/DisputeProgressBadge';
import { PAYMENT_ICONS } from '../constants/paymentIcons';
import { useFees } from '../context/FeeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OwnerPanel = ({ user, onLogout }) => {
  const { refreshFeeRates } = useFees();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'dashboard');

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t) setActiveTab(t);
  }, [searchParams]);
  const navigate = useNavigate();
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Owner-panel access control (per-admin permissions; owner = full access)
  const [myPerms, setMyPerms] = useState(null);
  const [myAllAccess, setMyAllAccess] = useState(true);
  useEffect(() => {
    axios.get(`${API}/owner/my-permissions`)
      .then((r) => { setMyPerms(r.data.permissions || {}); setMyAllAccess(!!r.data.all_access); })
      .catch(() => { setMyPerms(null); setMyAllAccess(true); });
  }, []);
  const canAccess = (id) => myAllAccess || !myPerms || (myPerms[id] && myPerms[id].access);
  const canEdit = (id) => myAllAccess || !myPerms || (myPerms[id] && myPerms[id].access && myPerms[id].level === 'edit');
  const [permAdmin, setPermAdmin] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [adminsLoading, setAdminsLoading] = useState(false);

  // Company Details
  const [companyDetails, setCompanyDetails] = useState({
    company_name: 'GoodJob PH',
    tagline: '',
    description: '',
    logo_url: '',
    website: '',
    contact_email: '',
    contact_phone: '',
    privacy_email: '',
    address: '',
    show_email: true,
    show_phone: true,
    show_address: true,
    show_support_chat: true,
    city: '',
    country: 'Philippines',
    registration_number: '',
    tax_id: '',
    social_facebook: '',
    social_twitter: '',
    social_instagram: '',
    social_linkedin: '',
    // Footer Content Pages
    about_us: '',
    privacy_policy: '',
    terms_of_service: '',
    success_stories: '',
    resources: ''
  });

  // App Configuration
  const [appConfig, setAppConfig] = useState({
    client_fee_rate: 10.0,
    provider_fee_rate: 5.0,
    platform_commission: 15.0,
    provider_share: 95.0,
    enable_registration: true,
    enable_bookings: true,
    enable_messaging: true,
    enable_reviews: true,
    require_barangay_clearance: true,
    require_payment_method: true,
    auto_confirm_hours: 24,
    max_services_per_provider: 50,
    max_images_per_service: 10,
    max_file_upload_size_mb: 5,
    maintenance_mode: false,
    maintenance_message: '',
    kyc_verification_mode: 'ai', // 'ai' for AI verification, 'admin' for manual admin verification
    fee_allocation: [
      { label: 'Tax', value: 1.7 },
      { label: 'Payment Processing', value: 2 },
      { label: 'Tech Infrastructure', value: 3.5 },
      { label: 'Customer Support', value: 2.5 },
      { label: 'Operation Cost', value: 2.3 },
      { label: 'Platform Margin', value: 3 },
    ]
  });

  // PayMongo Configuration
  const [paymongoConfig, setPaymongoConfig] = useState({
    paymongo_secret_key: '',
    paymongo_public_key: '',
    paymongo_webhook_secret: '',
    paymongo_source_account_number: '',
    paymongo_source_account_name: '',
    payouts_enabled: false,
    auto_payout_on_release: false,
  });
  const [paymongoSaving, setPaymongoSaving] = useState(false);
  const [showPaymongoKeys, setShowPaymongoKeys] = useState(false);

  // Redis (cache / real-time scaling) Configuration
  const [redisConfig, setRedisConfig] = useState({ configured: false, source: 'none', masked_url: null, connected: false, message: '', env_present: false });
  const [redisInput, setRedisInput] = useState('');
  const [redisSaving, setRedisSaving] = useState(false);
  const [redisTesting, setRedisTesting] = useState(false);
  const [redisTestResult, setRedisTestResult] = useState(null);

  const fetchRedisConfig = async () => {
    try {
      const res = await axios.get(`${API}/owner/redis-config`);
      setRedisConfig(res.data);
    } catch (e) {
      // non-fatal
    }
  };

  const testRedisConnection = async () => {
    setRedisTesting(true);
    setRedisTestResult(null);
    try {
      const res = await axios.post(`${API}/owner/redis-config/test`, { redis_url: redisInput.trim() });
      setRedisTestResult(res.data);
    } catch (e) {
      setRedisTestResult({ ok: false, message: e.response?.data?.detail || 'Test failed' });
    } finally {
      setRedisTesting(false);
    }
  };

  const saveRedisConfig = async () => {
    setRedisSaving(true);
    try {
      const res = await axios.put(`${API}/owner/redis-config`, { redis_url: redisInput.trim() });
      showSuccess(res.data.message || 'Redis configuration saved');
      setRedisInput('');
      setRedisTestResult(null);
      await fetchRedisConfig();
    } catch (e) {
      showError(e.response?.data?.detail || 'Failed to save Redis configuration');
    } finally {
      setRedisSaving(false);
    }
  };

  const clearRedisConfig = async () => {
    setRedisSaving(true);
    try {
      const res = await axios.put(`${API}/owner/redis-config`, { redis_url: '' });
      showSuccess(res.data.message || 'Redis URL cleared');
      setRedisInput('');
      setRedisTestResult(null);
      await fetchRedisConfig();
    } catch (e) {
      showError(e.response?.data?.detail || 'Failed to clear Redis configuration');
    } finally {
      setRedisSaving(false);
    }
  };

  // Code Editor
  const [codeFiles, setCodeFiles] = useState([]);
  const [selectedDirectory, setSelectedDirectory] = useState('backend');
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Admin Management
  const [newAdmin, setNewAdmin] = useState({ name: '', email: '', password: '' });

  // Users for management
  const [allUsers, setAllUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [userTotal, setUserTotal] = useState(0);
  const [userHasMore, setUserHasMore] = useState(false);
  const [userListLoading, setUserListLoading] = useState(false);
  const [loadingMoreUsers, setLoadingMoreUsers] = useState(false);
  // Announcements
  const [announcements, setAnnouncements] = useState([]);
  const [annForm, setAnnForm] = useState({
    title: '', body: '', image_url: '',
    target_clients: true, target_providers: true,
    show_text: true,
    link_enabled: false, link_url: '', link_label: 'Learn more',
    start_date: '', expiry_date: '',
  });
  const [annSubmitting, setAnnSubmitting] = useState(false);
  const [annUploading, setAnnUploading] = useState(false);
  
  // Edit User Modal
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  // Message User Modal
  const [msgModalUser, setMsgModalUser] = useState(null);
  const [msgSender, setMsgSender] = useState('');
  const [msgSubject, setMsgSubject] = useState('');
  const [msgBody, setMsgBody] = useState('');
  const [msgSending, setMsgSending] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [savingUser, setSavingUser] = useState(false);

  // Job Monitoring
  const [abandonedJobs, setAbandonedJobs] = useState([]);
  const [jobStats, setJobStats] = useState(null);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [processingJobs, setProcessingJobs] = useState(false);

  // Ban Appeals
  const [appeals, setAppeals] = useState([]);
  const [appealStats, setAppealStats] = useState(null);
  const [loadingAppeals, setLoadingAppeals] = useState(false);
  const [processingAppeal, setProcessingAppeal] = useState(null);

  // Reset Data
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  // Selective (targeted) Data Clear
  const [clearCategories, setClearCategories] = useState([]);
  const [selectedClearKeys, setSelectedClearKeys] = useState([]);
  const [clearResetBalances, setClearResetBalances] = useState(false);
  const [showSelectiveModal, setShowSelectiveModal] = useState(false);
  const [selectivePassword, setSelectivePassword] = useState('');
  const [selectiveClearing, setSelectiveClearing] = useState(false);
  const [selectiveResult, setSelectiveResult] = useState(null);

  const toggleClearKey = (key) => {
    setSelectedClearKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  // Support Tickets
  const [supportTickets, setSupportTickets] = useState([]);
  const [activeSupTicket, setActiveSupTicket] = useState(null);
  const [supMessages, setSupMessages] = useState([]);
  const [supReply, setSupReply] = useState('');
  const [supSending, setSupSending] = useState(false);
  const [supFilter, setSupFilter] = useState('all');
  const supMsgEndRef = React.useRef(null);

  // Disputes
  const [disputes, setDisputes] = useState([]);
  const [disputeStats, setDisputeStats] = useState(null);
  const [loadingDisputes, setLoadingDisputes] = useState(false);

  // Payment Methods
  const [paymentMethods, setPaymentMethods] = useState({
    gcash: true,
    qrph: true,
    maya: true,
    card: true,
    bank_transfer: true
  });
  const [savingPaymentMethods, setSavingPaymentMethods] = useState(false);
  // Provider payout methods (independent of client checkout methods)
  const [payoutMethods, setPayoutMethods] = useState({
    gcash: true,
    maya: true,
    bank_transfer: true
  });
  const [savingPayoutMethods, setSavingPayoutMethods] = useState(false);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolutionType, setResolutionType] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [penaltyPercentage, setPenaltyPercentage] = useState('');
  const [suspensionDays, setSuspensionDays] = useState('');
  const [refundToClient, setRefundToClient] = useState(false);
  const [processingResolution, setProcessingResolution] = useState(false);

  // Activity Logs
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityFilters, setActivityFilters] = useState({ admins: [], action_types: [] });
  const [activityFilter, setActivityFilter] = useState({ admin_id: '', action: '', start_date: '', end_date: '' });
  const [loadingActivity, setLoadingActivity] = useState(false);

  useEffect(() => {
    checkOwnerStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch job monitoring data when jobs tab is selected
  useEffect(() => {
    if (activeTab === 'jobs' && isOwner) {
      fetchJobMonitoringData();
      fetchAppeals();
    }
  }, [activeTab, isOwner]);

  // Fetch disputes when disputes tab is selected
  useEffect(() => {
    if (activeTab === 'disputes' && isOwner) {
      fetchDisputes();
    }
  }, [activeTab, isOwner]);

  // Fetch selective-clear categories when reset tab is selected
  useEffect(() => {
    if (activeTab === 'reset' && isOwner) {
      (async () => {
        try {
          const res = await axios.get(`${API}/owner/clear-data/categories`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          setClearCategories(res.data.categories || []);
        } catch (err) {
          // non-blocking
        }
      })();
    }
  }, [activeTab, isOwner]);

  // Fetch activity logs when activity tab is selected
  useEffect(() => {
    if (activeTab === 'activity' && isOwner) {
      fetchActivityLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isOwner, activityFilter]);

  // Fetch Redis config when the Configuration tab is opened
  useEffect(() => {
    if (activeTab === 'config' && isOwner) {
      fetchRedisConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isOwner]);

  // Fetch support tickets when tab is selected
  useEffect(() => {
    if (isOwner) fetchSupportTickets();
  }, [activeTab, isOwner]);

  // Poll for new messages when viewing a support ticket
  useEffect(() => {
    let interval;
    if (activeSupTicket && activeTab === 'support') {
      fetchSupMessages(activeSupTicket.id);
      interval = setInterval(() => fetchSupMessages(activeSupTicket.id), 5000);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [activeSupTicket, activeTab]);

  const checkOwnerStatus = async () => {
    try {
      const response = await axios.get(`${API}/owner/check`);
      const hasAccess = response.data.is_owner || response.data.is_admin;
      setIsOwner(hasAccess);
      if (hasAccess) {
        fetchAllData();
      }
    } catch (error) {
      setIsOwner(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllData = async () => {
    try {
      const [companyRes, configRes, paymentConfigRes, payoutRes] = await Promise.all([
        axios.get(`${API}/owner/company-details`),
        axios.get(`${API}/owner/app-config`),
        axios.get(`${API}/payments/config`),
        axios.get(`${API}/payout-methods/enabled`).catch(() => ({ data: {} }))
      ]);
      setCompanyDetails(companyRes.data);
      setAppConfig((prev) => ({
        ...prev,
        ...configRes.data,
        fee_allocation:
          Array.isArray(configRes.data.fee_allocation) && configRes.data.fee_allocation.length > 0
            ? configRes.data.fee_allocation
            : prev.fee_allocation,
      }));
      // Users are now loaded lazily + server-side paginated (see fetchUsers).
      // Set payment methods from config
      if (paymentConfigRes.data.enabled_payment_methods) {
        setPaymentMethods(paymentConfigRes.data.enabled_payment_methods);
      }
      // Set provider payout methods
      if (payoutRes.data.enabled_payout_methods) {
        setPayoutMethods(prev => ({ ...prev, ...payoutRes.data.enabled_payout_methods }));
      }
      // Set PayMongo config from app config
      if (configRes.data) {
        setPaymongoConfig({
          paymongo_secret_key: configRes.data.paymongo_secret_key || '',
          paymongo_public_key: configRes.data.paymongo_public_key || '',
          paymongo_webhook_secret: configRes.data.paymongo_webhook_secret || '',
          paymongo_source_account_number: configRes.data.paymongo_source_account_number || '',
          paymongo_source_account_name: configRes.data.paymongo_source_account_name || '',
          payouts_enabled: configRes.data.payouts_enabled ?? false,
          auto_payout_on_release: configRes.data.auto_payout_on_release ?? false,
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleSavePaymentMethods = async () => {
    setSavingPaymentMethods(true);
    try {
      await axios.put(`${API}/payments/methods`, {
        enabled_payment_methods: paymentMethods
      });
      showSuccess('Payment methods updated successfully!');
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to update payment methods');
    } finally {
      setSavingPaymentMethods(false);
    }
  };

  const togglePaymentMethod = (method) => {
    setPaymentMethods(prev => ({
      ...prev,
      [method]: !prev[method]
    }));
  };

  const handleSavePayoutMethods = async () => {
    setSavingPayoutMethods(true);
    try {
      await axios.put(`${API}/payout-methods`, {
        enabled_payout_methods: payoutMethods
      });
      showSuccess('Payout methods updated successfully!');
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to update payout methods');
    } finally {
      setSavingPayoutMethods(false);
    }
  };

  const togglePayoutMethod = (method) => {
    setPayoutMethods(prev => ({
      ...prev,
      [method]: !prev[method]
    }));
  };

  const handleSavePaymongoConfig = async () => {
    setPaymongoSaving(true);
    try {
      await axios.put(`${API}/owner/app-config`, {
        ...appConfig,
        paymongo_secret_key: paymongoConfig.paymongo_secret_key,
        paymongo_public_key: paymongoConfig.paymongo_public_key,
        paymongo_webhook_secret: paymongoConfig.paymongo_webhook_secret,
        paymongo_source_account_number: paymongoConfig.paymongo_source_account_number,
        paymongo_source_account_name: paymongoConfig.paymongo_source_account_name,
        payouts_enabled: paymongoConfig.payouts_enabled,
        auto_payout_on_release: paymongoConfig.auto_payout_on_release,
      });
      showSuccess('PayMongo configuration saved successfully!');
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to save PayMongo config');
    } finally {
      setPaymongoSaving(false);
    }
  };

  const showSuccess = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(''), 3000);
  };

  const showError = (message) => {
    // Handle various error formats (string, object, array)
    let errorMessage = 'An error occurred';
    if (typeof message === 'string') {
      errorMessage = message;
    } else if (Array.isArray(message)) {
      // Pydantic validation errors come as array
      errorMessage = message.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
    } else if (message && typeof message === 'object') {
      errorMessage = message.msg || message.message || message.detail || JSON.stringify(message);
    }
    setError(errorMessage);
    setTimeout(() => setError(''), 5000);
  };

  // Fetch abandoned jobs data
  const fetchJobMonitoringData = async () => {
    setLoadingJobs(true);
    try {
      const [abandonedRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/abandoned-jobs`),
        axios.get(`${API}/admin/abandonment-stats`)
      ]);
      setAbandonedJobs(abandonedRes.data);
      setJobStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching job monitoring data:', error);
    } finally {
      setLoadingJobs(false);
    }
  };

  // Manually trigger job expiration processing
  const processExpiredJobs = async () => {
    setProcessingJobs(true);
    try {
      const response = await axios.post(`${API}/jobs/process-expired`);
      const results = response.data;
      showSuccess(
        `Processed expired jobs: ${results.bidding_closed} closed, ${results.jobs_abandoned} abandoned, ` +
        `${results.users_warned} warned, ${results.users_banned} banned`
      );
      // Refresh data
      fetchJobMonitoringData();
      fetchAllData();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to process expired jobs');
    } finally {
      setProcessingJobs(false);
    }
  };

  // Reset user abandonment count
  const resetUserAbandonmentCount = async (userId) => {
    try {
      await axios.post(`${API}/admin/reset-abandonment/${userId}`);
      showSuccess('User abandonment count reset successfully');
      fetchJobMonitoringData();
      fetchAllData();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to reset abandonment count');
    }
  };

  // Fetch ban appeals
  const fetchAppeals = async () => {
    setLoadingAppeals(true);
    try {
      const [appealsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/appeals`),
        axios.get(`${API}/admin/appeals/stats`)
      ]);
      setAppeals(appealsRes.data);
      setAppealStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching appeals:', error);
    } finally {
      setLoadingAppeals(false);
    }
  };

  // Approve appeal
  const handleApproveAppeal = async (appealId, adminNotes) => {
    setProcessingAppeal(appealId);
    try {
      await axios.post(`${API}/admin/appeals/${appealId}/approve`, null, {
        params: { admin_notes: adminNotes || 'Appeal approved' }
      });
      showSuccess('Appeal approved. User has been unbanned.');
      fetchAppeals();
      fetchJobMonitoringData();
      fetchAllData();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to approve appeal');
    } finally {
      setProcessingAppeal(null);
    }
  };

  // Reject appeal
  const handleRejectAppeal = async (appealId, adminNotes) => {
    setProcessingAppeal(appealId);
    try {
      await axios.post(`${API}/admin/appeals/${appealId}/reject`, null, {
        params: { admin_notes: adminNotes || 'Appeal rejected' }
      });
      showSuccess('Appeal rejected. User remains banned.');
      fetchAppeals();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to reject appeal');
    } finally {
      setProcessingAppeal(null);
    }
  };

  // Fetch disputes
  const fetchDisputes = async () => {
    setLoadingDisputes(true);
    try {
      const [disputesRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/disputes`),
        axios.get(`${API}/admin/disputes/stats`)
      ]);
      setDisputes(disputesRes.data);
      setDisputeStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching disputes:', error);
    } finally {
      setLoadingDisputes(false);
    }
  };

  const fetchActivityLogs = async () => {
    setLoadingActivity(true);
    try {
      const params = new URLSearchParams();
      if (activityFilter.admin_id) params.append('admin_id', activityFilter.admin_id);
      if (activityFilter.action) params.append('action', activityFilter.action);
      if (activityFilter.start_date) params.append('start_date', activityFilter.start_date);
      if (activityFilter.end_date) params.append('end_date', activityFilter.end_date);
      const res = await axios.get(`${API}/admin/activity-logs?${params.toString()}`);
      setActivityLogs(res.data.logs || []);
      setActivityFilters(res.data.filters || { admins: [], action_types: [] });
    } catch (error) {
      console.error('Error fetching activity logs:', error);
    } finally {
      setLoadingActivity(false);
    }
  };

  // Support tickets functions
  const fetchSupportTickets = async () => {
    try {
      const res = await axios.get(`${API}/support/tickets`);
      setSupportTickets(res.data);
    } catch (error) {
      console.error('Error fetching support tickets:', error);
    }
  };

  const fetchSupMessages = async (ticketId) => {
    try {
      const res = await axios.get(`${API}/support/tickets/${ticketId}/messages`);
      setSupMessages(res.data);
      setTimeout(() => supMsgEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch {}
  };

  const sendSupReply = async () => {
    if (!supReply.trim() || !activeSupTicket) return;
    setSupSending(true);
    try {
      await axios.post(`${API}/support/tickets/${activeSupTicket.id}/messages`, { message: supReply });
      setSupReply('');
      fetchSupMessages(activeSupTicket.id);
    } catch {}
    setSupSending(false);
  };

  const closeSupTicket = async (ticketId) => {
    try {
      await axios.put(`${API}/support/tickets/${ticketId}/close`);
      fetchSupportTickets();
      if (activeSupTicket?.id === ticketId) setActiveSupTicket(prev => ({ ...prev, status: 'closed' }));
      setSuccess('Ticket closed');
    } catch {}
  };

  const reopenSupTicket = async (ticketId) => {
    try {
      await axios.put(`${API}/support/tickets/${ticketId}/reopen`);
      fetchSupportTickets();
      if (activeSupTicket?.id === ticketId) setActiveSupTicket(prev => ({ ...prev, status: 'open' }));
      setSuccess('Ticket reopened');
    } catch {}
  };

  const deleteSupTicket = async (ticketId) => {
    try {
      await axios.delete(`${API}/support/tickets/${ticketId}`);
      fetchSupportTickets();
      if (activeSupTicket?.id === ticketId) { setActiveSupTicket(null); setSupMessages([]); }
      setSuccess('Ticket deleted');
    } catch {}
  };

  // Open resolve modal
  const openResolveModal = (dispute) => {
    setSelectedDispute(dispute);
    setResolutionType('');
    setResolutionNotes('');
    setPenaltyPercentage('');
    setSuspensionDays('');
    setRefundToClient(false);
    setShowResolveModal(true);
  };

  // Resolve dispute
  const handleResolveDispute = async () => {
    if (!resolutionType) {
      showError('Please select a resolution type');
      return;
    }
    if (!resolutionNotes.trim()) {
      showError('Please provide resolution notes');
      return;
    }
    if (resolutionType === 'payment_deduction' && (!penaltyPercentage || penaltyPercentage <= 0 || penaltyPercentage > 100)) {
      showError('Please enter a valid penalty percentage (1-100)');
      return;
    }
    if (resolutionType === 'suspension' && (!suspensionDays || suspensionDays <= 0)) {
      showError('Please enter valid suspension days');
      return;
    }

    setProcessingResolution(true);
    try {
      await axios.post(`${API}/admin/disputes/${selectedDispute.id}/resolve`, {
        resolution_type: resolutionType,
        notes: resolutionNotes,
        penalty_percentage: resolutionType === 'payment_deduction' ? parseFloat(penaltyPercentage) : null,
        suspension_days: resolutionType === 'suspension' ? parseInt(suspensionDays) : null,
        refund_to_client: refundToClient
      });
      showSuccess('Dispute resolved successfully');
      setShowResolveModal(false);
      fetchDisputes();
      fetchAllData();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to resolve dispute');
    } finally {
      setProcessingResolution(false);
    }
  };

  const handleSaveCompanyDetails = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/owner/company-details`, companyDetails);
      showSuccess('Company details saved successfully!');
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to save company details');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAppConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/owner/app-config`, appConfig);
      await refreshFeeRates();
      showSuccess('Configuration saved successfully!');
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        // Pydantic validation errors
        showError(errorDetail);
      } else if (typeof errorDetail === 'string') {
        showError(errorDetail);
      } else {
        showError('Failed to save configuration');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    if (!newAdmin.name || !newAdmin.email || !newAdmin.password) {
      showError('Please fill in all admin fields');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/owner/create-admin?email=${encodeURIComponent(newAdmin.email)}&password=${encodeURIComponent(newAdmin.password)}&name=${encodeURIComponent(newAdmin.name)}`);
      showSuccess('Admin created successfully!');
      setNewAdmin({ name: '', email: '', password: '' });
      fetchAllData();
      fetchAdmins();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to create admin');
    } finally {
      setSaving(false);
    }
  };

  const fetchCodeFiles = async (directory) => {
    setLoadingFiles(true);
    try {
      const response = await axios.get(`${API}/owner/code-files?directory=${directory}`);
      setCodeFiles(response.data.files);
      setSelectedDirectory(directory);
      setSelectedFile(null);
      setFileContent('');
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to load files');
    } finally {
      setLoadingFiles(false);
    }
  };

  const loadFileContent = async (filePath) => {
    setLoadingFiles(true);
    try {
      const response = await axios.get(`${API}/owner/code-file?directory=${selectedDirectory}&file_path=${encodeURIComponent(filePath)}`);
      setSelectedFile(filePath);
      setFileContent(response.data.content);
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to load file');
    } finally {
      setLoadingFiles(false);
    }
  };

  const saveFileContent = async () => {
    if (!selectedFile) return;
    // Show password confirmation modal
    setShowPasswordModal(true);
    setPasswordError('');
    setConfirmPassword('');
  };

  const confirmSaveFile = async () => {
    if (!confirmPassword) {
      setPasswordError('Please enter your password');
      return;
    }

    setSaving(true);
    setPasswordError('');
    
    try {
      // First verify password
      const verifyResponse = await axios.post(`${API}/owner/verify-password`, {
        password: confirmPassword
      });
      
      if (!verifyResponse.data.verified) {
        setPasswordError('Incorrect password');
        setSaving(false);
        return;
      }
      
      // Password verified, proceed with save
      await axios.put(`${API}/owner/code-file?directory=${selectedDirectory}&file_path=${encodeURIComponent(selectedFile)}&content=${encodeURIComponent(fileContent)}`);
      showSuccess('File saved successfully! (Backup created)');
      setShowPasswordModal(false);
      setConfirmPassword('');
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        setPasswordError('Incorrect password');
      } else {
        showError(error.response?.data?.detail || 'Failed to save file');
        setShowPasswordModal(false);
      }
    } finally {
      setSaving(false);
    }
  };

  const USER_PAGE_SIZE = 25;
  const fetchUsers = async (reset = true) => {
    if (reset) setUserListLoading(true);
    else setLoadingMoreUsers(true);
    try {
      const skip = reset ? 0 : allUsers.length;
      const params = new URLSearchParams();
      if (userSearch.trim()) params.set('q', userSearch.trim());
      params.set('skip', String(skip));
      params.set('limit', String(USER_PAGE_SIZE));
      const res = await axios.get(`${API}/admin/users/paginated?${params.toString()}`);
      const list = res.data.users || [];
      setAllUsers((prev) => (reset ? list : [...prev, ...list]));
      setUserTotal(res.data.total || 0);
      setUserHasMore(!!res.data.has_more);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setUserListLoading(false);
      setLoadingMoreUsers(false);
    }
  };

  // Load users lazily when the tab opens; debounce server-side search.
  useEffect(() => {
    if (activeTab !== 'users') return;
    const t = setTimeout(() => { fetchUsers(true); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSearch, activeTab]);

  // Administrators load from a dedicated endpoint (independent of the paginated users list).
  const fetchAdmins = async () => {
    setAdminsLoading(true);
    try {
      const res = await axios.get(`${API}/owner/admins`);
      setAdmins(res.data.admins || []);
    } catch (error) {
      console.error('Error fetching admins:', error);
    } finally {
      setAdminsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'admins') fetchAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // ---- Announcements ----
  const fetchAnnouncements = async () => {
    try {
      const res = await axios.get(`${API}/admin/announcements`);
      setAnnouncements(res.data || []);
    } catch (error) {
      console.error('Error fetching announcements:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'announcements') fetchAnnouncements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleAnnImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { showError('Image must be less than 5MB'); return; }
    setAnnUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAnnForm((f) => ({ ...f, image_url: res.data.url }));
      showSuccess('Image uploaded');
    } catch (error) {
      showError('Failed to upload image');
    } finally {
      setAnnUploading(false);
    }
  };

  const handleCreateAnnouncement = async () => {
    if (!annForm.title.trim() && !annForm.body.trim() && !annForm.image_url) {
      showError('Add a title, message, or image'); return;
    }
    if (!annForm.target_clients && !annForm.target_providers) {
      showError('Select at least one audience (Clients or Providers)'); return;
    }
    setAnnSubmitting(true);
    try {
      const payload = {
        ...annForm,
        start_date: annForm.start_date ? `${annForm.start_date}T00:00:00` : null,
        expiry_date: annForm.expiry_date ? `${annForm.expiry_date}T23:59:59` : null,
      };
      await axios.post(`${API}/admin/announcements`, payload);
      showSuccess('Announcement posted — users will be notified.');
      setAnnForm({ title: '', body: '', image_url: '', target_clients: true, target_providers: true, show_text: true, link_enabled: false, link_url: '', link_label: 'Learn more', start_date: '', expiry_date: '' });
      fetchAnnouncements();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to post announcement');
    } finally {
      setAnnSubmitting(false);
    }
  };

  const toggleAnnouncementActive = async (a) => {
    try {
      await axios.put(`${API}/admin/announcements/${a.id}`, { active: !a.active });
      fetchAnnouncements();
    } catch (error) {
      showError('Failed to update announcement');
    }
  };

  const deleteAnnouncement = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await axios.delete(`${API}/admin/announcements/${id}`);
      showSuccess('Announcement deleted');
      fetchAnnouncements();
    } catch (error) {
      showError('Failed to delete announcement');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This will also delete all their data.')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      showSuccess('User deleted successfully');
      fetchAllData();
      fetchUsers(true);
      if (activeTab === 'admins') fetchAdmins();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleRestoreAccount = async (userId, userName) => {
    if (!window.confirm(`Restore ${userName || 'this'}'s account? They will be able to log in again and their listings will be reactivated.`)) return;
    try {
      const res = await axios.post(`${API}/admin/users/${userId}/restore-account`);
      showSuccess(res.data?.message || 'Account restored successfully');
      fetchAllData();
      fetchUsers(true);
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to restore account');
    }
  };

  const openEditUserModal = (userToEdit) => {
    setEditingUser({ ...userToEdit });
    setNewPassword('');
    setShowEditUserModal(true);
  };

  const openMessageUserModal = (u) => {
    setMsgModalUser(u);
    setMsgSender('');
    setMsgSubject('');
    setMsgBody('');
  };

  const handleSendUserMessage = async () => {
    if (!msgBody.trim()) { showError('Message is required'); return; }
    setMsgSending(true);
    try {
      await axios.post(`${API}/admin/users/${msgModalUser.id}/message`, {
        message: msgBody,
        sender_name: msgSender,
        subject: msgSubject,
      });
      showSuccess(`Message sent to ${msgModalUser.name || 'user'}`);
      setMsgModalUser(null);
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to send message');
    }
    setMsgSending(false);
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;
    
    setSavingUser(true);
    try {
      // Build query params for all editable fields
      const params = new URLSearchParams();
      if (editingUser.name) params.append('name', editingUser.name);
      if (editingUser.email) params.append('email', editingUser.email);
      if (editingUser.phone) params.append('phone', editingUser.phone);
      if (editingUser.bio) params.append('bio', editingUser.bio);
      
      // Send available_roles as comma-separated list
      if (editingUser.available_roles && editingUser.available_roles.length > 0) {
        params.append('available_roles', editingUser.available_roles.join(','));
      }
      
      if (editingUser.status) params.append('status', editingUser.status);
      if (editingUser.currency) params.append('currency', editingUser.currency);
      params.append('id_verified', editingUser.id_verified ? 'true' : 'false');
      
      await axios.put(`${API}/owner/users/${editingUser.id}?${params.toString()}`);

      // When suspending/banning, persist reason/dates/internal notes via the
      // moderation-aware status endpoint (temporary suspensions auto-lift).
      if (editingUser.status === 'suspended' || editingUser.status === 'banned') {
        const sp = new URLSearchParams();
        sp.append('status', editingUser.status);
        if (editingUser.mod_reason) sp.append('reason', editingUser.mod_reason);
        if (editingUser.mod_notes) sp.append('internal_notes', editingUser.mod_notes);
        if (editingUser.mod_effective) sp.append('effective_date', new Date(editingUser.mod_effective).toISOString());
        if (editingUser.status === 'suspended' && editingUser.mod_expiration) {
          sp.append('expiration_date', new Date(editingUser.mod_expiration).toISOString());
        }
        await axios.put(`${API}/admin/users/${editingUser.id}/status?${sp.toString()}`);
      }
      
      // Update password if provided
      if (newPassword && newPassword.length >= 6) {
        await axios.put(`${API}/owner/users/${editingUser.id}/password?new_password=${encodeURIComponent(newPassword)}`);
      }
      
      setShowEditUserModal(false);
      setEditingUser(null);
      setNewPassword('');
      showSuccess('User updated successfully');
      fetchAllData();
      fetchUsers(true);
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSavingUser(false);
    }
  };

  // Handle role checkbox toggle
  const handleRoleToggle = (role) => {
    if (!editingUser) return;
    
    const currentRoles = editingUser.available_roles || [];
    let newRoles;
    
    if (currentRoles.includes(role)) {
      // Remove role (but keep at least one)
      newRoles = currentRoles.filter(r => r !== role);
      if (newRoles.length === 0) {
        showError('User must have at least one role');
        return;
      }
    } else {
      // Add role (max 3)
      if (currentRoles.length >= 3) {
        showError('User can have a maximum of 3 roles');
        return;
      }
      newRoles = [...currentRoles, role];
    }
    
    setEditingUser({ ...editingUser, available_roles: newRoles });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <div className="text-xl text-white">Loading...</div>
        </div>
      </div>
    );
  }

  if (!isOwner) {
    return (
      <div className="min-h-screen bg-gray-900">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-red-500 mb-4">🚫 Access Denied</h2>
            <p className="text-gray-400 mb-4">This panel is restricted to the platform owner only.</p>
            <Link to="/dashboard" className="text-blue-400 hover:underline">
              Return to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'earnings', label: 'Platform Earnings', icon: '💰' },
    { id: 'health', label: 'System Health', icon: '🩺' },
    { id: 'company', label: 'Company Details', icon: '🏢' },
    { id: 'config', label: 'Configuration', icon: '⚙️' },
    { id: 'payments', label: 'Payment Methods', icon: '💳' },
    { id: 'users', label: 'User Management', icon: '👥' },
    { id: 'announcements', label: 'Announcements', icon: '📢' },
    { id: 'admins', label: 'Administrators', icon: '🔐' },
    { id: 'verification', label: 'ID Verification', icon: '🪪' },
    { id: 'moderation', label: 'Reports', icon: '🚩' },
    { id: 'jobs', label: 'Job Monitoring', icon: '📋' },
    { id: 'disputes', label: 'Disputes', icon: '⚖️' },
    { id: 'activity', label: 'Activity Log', icon: '📝' },
    { id: 'support', label: 'Support Tickets', icon: '🎧' },
    { id: 'code', label: 'Code Editor', icon: '💻' },
    { id: 'database', label: 'Database', icon: '🗄️' },
    { id: 'reset', label: 'Reset Data', icon: '🗑️' }
  ];

  // OTP Configuration Component
  const OTPConfigSection = () => {
    const [otpConfig, setOtpConfig] = React.useState({
      enabled: false,
      otp_mode: 'every_login',
      semaphore_api_key: '',
      semaphore_api_key_masked: '',
      sender_name: 'GoodJob',
    });
    const [otpLoading, setOtpLoading] = React.useState(true);
    const [otpSaving, setOtpSaving] = React.useState(false);
    const [showApiKey, setShowApiKey] = React.useState(false);
    const [testPhone, setTestPhone] = React.useState('');
    const [testSending, setTestSending] = React.useState(false);

    React.useEffect(() => {
      const token = localStorage.getItem('token');
      axios.get(`${API}/otp/config`, { headers: { Authorization: `Bearer ${token}` }})
        .then(res => { setOtpConfig(res.data); setOtpLoading(false); })
        .catch(() => setOtpLoading(false));
    }, []);

    const handleSaveOTP = async () => {
      setOtpSaving(true);
      try {
        const token = localStorage.getItem('token');
        await axios.put(`${API}/otp/config`, {
          enabled: otpConfig.enabled,
          otp_mode: otpConfig.otp_mode,
          semaphore_api_key: otpConfig.semaphore_api_key || null,
          sender_name: otpConfig.sender_name,
        }, { headers: { Authorization: `Bearer ${token}` }});
        showSuccess('OTP configuration saved!');
        // Refresh to get masked key
        const res = await axios.get(`${API}/otp/config`, { headers: { Authorization: `Bearer ${token}` }});
        setOtpConfig(res.data);
      } catch (err) {
        showError(err.response?.data?.detail || 'Failed to save OTP config');
      } finally {
        setOtpSaving(false);
      }
    };

    const handleTestSMS = async () => {
      if (!testPhone.trim()) { showError('Enter a phone number to test'); return; }
      setTestSending(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.post(`${API}/otp/test-sms`, { phone_number: testPhone }, { headers: { Authorization: `Bearer ${token}` }});
        showSuccess(res.data.message || 'Test SMS sent!');
      } catch (err) {
        showError(err.response?.data?.detail || 'Test SMS failed');
      } finally {
        setTestSending(false);
      }
    };

    if (otpLoading) return <div className="bg-gray-700 rounded-lg p-4 animate-pulse h-32" />;

    return (
      <div className="bg-gray-700 rounded-lg p-4" data-testid="otp-config-section">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-white">📱 OTP Verification (Semaphore SMS)</h3>
            <p className="text-gray-400 text-sm">Require SMS one-time password during login for extra security.</p>
          </div>
          <button
            onClick={() => {
              if (!otpConfig.semaphore_api_key_masked && !otpConfig.semaphore_api_key) {
                showError('Please enter your Semaphore API key first');
                return;
              }
              setOtpConfig({...otpConfig, enabled: !otpConfig.enabled});
            }}
            className={`relative inline-flex h-7 w-14 shrink-0 items-center rounded-full transition ${otpConfig.enabled ? 'bg-green-500' : 'bg-gray-500'}`}
            data-testid="otp-toggle"
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${otpConfig.enabled ? 'translate-x-8' : 'translate-x-1'}`} />
          </button>
        </div>

        <div className="space-y-4">
          {/* OTP Mode */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">OTP Mode</label>
            <select
              value={otpConfig.otp_mode}
              onChange={(e) => setOtpConfig({...otpConfig, otp_mode: e.target.value})}
              className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white"
              data-testid="otp-mode-select"
            >
              <option value="every_login">Every Login (always require OTP)</option>
              <option value="new_device">New Device Only (remember trusted devices)</option>
            </select>
          </div>

          {/* Semaphore API Key */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Semaphore API Key</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type={showApiKey ? 'text' : 'password'}
                  value={otpConfig.semaphore_api_key}
                  onChange={(e) => setOtpConfig({...otpConfig, semaphore_api_key: e.target.value})}
                  placeholder={otpConfig.semaphore_api_key_masked || 'Enter your Semaphore API key'}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white pr-16"
                  data-testid="semaphore-api-key-input"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white text-xs px-2 py-1"
                >
                  {showApiKey ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
            <p className="text-gray-500 text-xs mt-1">
              Get your API key from <a href="https://semaphore.co/account" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">semaphore.co/account</a>
            </p>
          </div>

          {/* Sender Name */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Sender Name</label>
            <input
              type="text"
              value={otpConfig.sender_name}
              onChange={(e) => setOtpConfig({...otpConfig, sender_name: e.target.value})}
              placeholder="GoodJob"
              maxLength={11}
              className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white"
              data-testid="otp-sender-name-input"
            />
            <p className="text-gray-500 text-xs mt-1">Max 11 characters. This appears as the SMS sender.</p>
          </div>

          {/* Test SMS */}
          <div className="bg-gray-800 rounded-lg p-3">
            <label className="block text-gray-300 text-sm font-medium mb-2">Test SMS</label>
            <div className="flex gap-2">
              <input
                type="tel"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="09XX XXX XXXX"
                className="flex-1 min-w-0 px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white"
                data-testid="otp-test-phone-input"
              />
              <button
                onClick={handleTestSMS}
                disabled={testSending || !testPhone.trim()}
                className="shrink-0 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 text-sm whitespace-nowrap"
                data-testid="otp-test-sms-btn"
              >
                {testSending ? 'Sending...' : 'Send Test'}
              </button>
            </div>
          </div>

          {/* Status indicator */}
          <div className={`px-4 py-2 rounded-lg text-sm font-medium ${otpConfig.enabled ? 'bg-green-900/30 text-green-400 border border-green-700' : 'bg-gray-800 text-gray-400 border border-gray-600'}`}>
            {otpConfig.enabled ? 'OTP is ACTIVE — users will receive SMS codes during login' : 'OTP is OFF — standard password login only'}
          </div>

          <button
            onClick={handleSaveOTP}
            disabled={otpSaving}
            className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-lg hover:shadow-lg font-semibold disabled:opacity-50 w-full"
            data-testid="save-otp-config-btn"
          >
            {otpSaving ? 'Saving...' : 'Save OTP Configuration'}
          </button>
        </div>
      </div>
    );
  };

  // Email Configuration Component (Resend)
  const EmailConfigSection = () => {
    const [emailConfig, setEmailConfig] = React.useState({
      enabled: false,
      sender_email: 'noreply@goodjobservice.com',
      sender_name: 'GoodJob PH',
    });
    const [emailLoading, setEmailLoading] = React.useState(true);
    const [emailSaving, setEmailSaving] = React.useState(false);
    const [testingEmail, setTestingEmail] = React.useState(false);
    const [testRecipient, setTestRecipient] = React.useState('');

    React.useEffect(() => {
      axios.get(`${API}/owner/email-config`).then(res => {
        setEmailConfig(res.data);
        setEmailLoading(false);
      }).catch(() => setEmailLoading(false));
    }, []);

    const saveEmailConfig = async () => {
      setEmailSaving(true);
      try {
        await axios.put(`${API}/owner/email-config`, emailConfig);
        showSuccess('Email configuration saved');
      } catch (err) {
        showError(err.response?.data?.detail || 'Failed to save');
      } finally {
        setEmailSaving(false);
      }
    };

    const sendTestEmail = async () => {
      if (!testRecipient) {
        showError('Enter a recipient email');
        return;
      }
      setTestingEmail(true);
      try {
        const res = await axios.post(`${API}/owner/test-email`, { recipient: testRecipient });
        showSuccess(res.data.message);
      } catch (err) {
        showError(err.response?.data?.detail || 'Failed to send test email');
      } finally {
        setTestingEmail(false);
      }
    };

    if (emailLoading) return <div className="text-gray-400 py-4">Loading email config...</div>;

    return (
      <div className="bg-gray-700/50 rounded-xl p-6 mt-6" data-testid="email-config-section">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
            Email Notifications (Resend)
          </h3>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={emailConfig.enabled}
              onChange={(e) => setEmailConfig({...emailConfig, enabled: e.target.checked})}
              className="sr-only peer"
              data-testid="email-enabled-toggle"
            />
            <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
          </label>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-1">Sender Name</label>
              <input
                type="text"
                value={emailConfig.sender_name}
                onChange={(e) => setEmailConfig({...emailConfig, sender_name: e.target.value})}
                className="w-full px-4 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="GoodJob PH"
                data-testid="email-sender-name"
              />
            </div>
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-1">Sender Email</label>
              <input
                type="email"
                value={emailConfig.sender_email}
                onChange={(e) => setEmailConfig({...emailConfig, sender_email: e.target.value})}
                className="w-full px-4 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="noreply@goodjobservice.com"
                data-testid="email-sender-address"
              />
            </div>
          </div>

          <button
            onClick={saveEmailConfig}
            disabled={emailSaving}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold transition disabled:opacity-50"
            data-testid="save-email-config-btn"
          >
            {emailSaving ? 'Saving...' : 'Save Email Config'}
          </button>

          {/* Test Email */}
          <div className="bg-gray-800/50 rounded-lg p-4 mt-4">
            <p className="text-gray-300 text-sm font-medium mb-2">Send Test Email</p>
            <div className="flex gap-2">
              <input
                type="email"
                value={testRecipient}
                onChange={(e) => setTestRecipient(e.target.value)}
                className="flex-1 min-w-0 px-4 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="recipient@example.com"
                data-testid="test-email-recipient"
              />
              <button
                onClick={sendTestEmail}
                disabled={testingEmail}
                className="shrink-0 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold transition disabled:opacity-50 whitespace-nowrap"
                data-testid="send-test-email-btn"
              >
                {testingEmail ? 'Sending...' : 'Send Test'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };


  return (
    <div className="min-h-screen bg-gray-900" data-testid="owner-panel">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            👑 Owner Control Panel
          </h1>
          <p className="text-gray-400 mt-1">Welcome, {user?.name}. Full platform control access.</p>
        </div>

        {/* Messages */}
        {success && (
          <div className="mb-4 p-4 bg-green-900 border border-green-500 text-green-300 rounded-lg">
            ✓ {success}
          </div>
        )}
        {error && (
          <div className="mb-4 p-4 bg-red-900 border border-red-500 text-red-300 rounded-lg">
            ✗ {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-gray-800 rounded-lg p-4 sticky top-4">
              <nav className="space-y-2">
                {tabs.filter((tab) => canAccess(tab.id)).map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full text-left px-4 py-3 rounded-lg font-medium transition ${
                      activeTab === tab.id
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                        : 'text-gray-300 hover:bg-gray-700'
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    <span className="mr-2">{tab.icon}</span>
                    {tab.label}
                    {tab.id === 'support' && supportTickets.filter(t => t.status === 'open').length > 0 && (
                      <span className="ml-2 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full" data-testid="support-tab-badge">
                        {supportTickets.filter(t => t.status === 'open').length}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-4">
            <div className="bg-gray-800 rounded-lg p-6 overflow-x-hidden">
              {!canAccess(activeTab) && (
                <div className="bg-red-900/40 border border-red-700 text-red-200 rounded-lg px-4 py-3 mb-4" data-testid="no-access-banner">
                  🔒 You don't have access to this section. Please contact the owner.
                </div>
              )}
              {canAccess(activeTab) && !canEdit(activeTab) && (
                <div className="bg-amber-900/40 border border-amber-700 text-amber-200 rounded-lg px-4 py-2.5 mb-4 text-sm flex items-center gap-2" data-testid="view-only-banner">
                  👁 View-only access — changes here are disabled by the owner.
                </div>
              )}
              {/* Dashboard */}
              {activeTab === 'dashboard' && (
                <div data-testid="dashboard-section">
                  <h2 className="text-2xl font-bold text-white mb-6">Platform Overview</h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-gray-700 rounded-lg p-4">
                      <div className="text-3xl font-bold text-blue-400">{allUsers.length}</div>
                      <div className="text-gray-400 text-sm">Total Users</div>
                    </div>
                    <div className="bg-gray-700 rounded-lg p-4">
                      <div className="text-3xl font-bold text-green-400">
                        {allUsers.filter(u => u.role === 'provider').length}
                      </div>
                      <div className="text-gray-400 text-sm">Providers</div>
                    </div>
                    <div className="bg-gray-700 rounded-lg p-4">
                      <div className="text-3xl font-bold text-purple-400">
                        {allUsers.filter(u => u.role === 'client').length}
                      </div>
                      <div className="text-gray-400 text-sm">Clients</div>
                    </div>
                    <div className="bg-gray-700 rounded-lg p-4">
                      <div className="text-3xl font-bold text-yellow-400">
                        {allUsers.filter(u => u.id_verification_status === 'pending').length}
                      </div>
                      <div className="text-gray-400 text-sm">Pending Verification</div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Link to="/admin" className="bg-gray-700 p-4 rounded-lg hover:bg-gray-600 transition">
                      <h3 className="text-white font-bold">📈 Full Admin Dashboard</h3>
                      <p className="text-gray-400 text-sm">View detailed analytics and reports</p>
                    </Link>
                    <Link to="/admin/id-verification" className="bg-gray-700 p-4 rounded-lg hover:bg-gray-600 transition">
                      <h3 className="text-white font-bold">🪪 ID Verification Queue</h3>
                      <p className="text-gray-400 text-sm">Review pending user verifications</p>
                    </Link>
                  </div>
                </div>
              )}

              {/* Platform Earnings */}
              {activeTab === 'earnings' && <PlatformEarningsTab />}

              {/* System Health */}
              {activeTab === 'health' && <SystemHealthTab />}

              {/* Company Details */}
              {activeTab === 'company' && (
                <div data-testid="company-section">
                  <h2 className="text-2xl font-bold text-white mb-6">Company Details</h2>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-300 text-sm font-medium mb-2">Company Name</label>
                        <input
                          type="text"
                          value={companyDetails.company_name}
                          onChange={(e) => setCompanyDetails({...companyDetails, company_name: e.target.value})}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-300 text-sm font-medium mb-2">Tagline</label>
                        <input
                          type="text"
                          value={companyDetails.tagline || ''}
                          onChange={(e) => setCompanyDetails({...companyDetails, tagline: e.target.value})}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-gray-300 text-sm font-medium mb-2">Description</label>
                      <textarea
                        value={companyDetails.description || ''}
                        onChange={(e) => setCompanyDetails({...companyDetails, description: e.target.value})}
                        rows="3"
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-gray-300 text-sm font-medium">Email</label>
                          <button
                            type="button"
                            onClick={() => setCompanyDetails({...companyDetails, show_email: !companyDetails.show_email})}
                            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${companyDetails.show_email !== false ? 'bg-blue-600' : 'bg-gray-600'}`}
                            data-testid="toggle-show-email"
                          >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${companyDetails.show_email !== false ? 'translate-x-6' : 'translate-x-1'}`} />
                          </button>
                        </div>
                        <input
                          type="email"
                          value={companyDetails.contact_email || ''}
                          onChange={(e) => setCompanyDetails({...companyDetails, contact_email: e.target.value})}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-gray-300 text-sm font-medium">Phone</label>
                          <button
                            type="button"
                            onClick={() => setCompanyDetails({...companyDetails, show_phone: !companyDetails.show_phone})}
                            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${companyDetails.show_phone !== false ? 'bg-blue-600' : 'bg-gray-600'}`}
                            data-testid="toggle-show-phone"
                          >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${companyDetails.show_phone !== false ? 'translate-x-6' : 'translate-x-1'}`} />
                          </button>
                        </div>
                        <input
                          type="tel"
                          value={companyDetails.contact_phone || ''}
                          onChange={(e) => setCompanyDetails({...companyDetails, contact_phone: e.target.value})}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-gray-300 text-sm font-medium">Address</label>
                        <button
                          type="button"
                          onClick={() => setCompanyDetails({...companyDetails, show_address: !companyDetails.show_address})}
                          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${companyDetails.show_address !== false ? 'bg-blue-600' : 'bg-gray-600'}`}
                          data-testid="toggle-show-address"
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${companyDetails.show_address !== false ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>
                      <input
                        type="text"
                        value={companyDetails.address || ''}
                        onChange={(e) => setCompanyDetails({...companyDetails, address: e.target.value})}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div className="bg-gray-700/40 border border-gray-700 rounded-lg p-4">
                      <p className="text-gray-200 text-sm font-medium mb-1">Support "Contact Us" options</p>
                      <p className="text-gray-400 text-xs mb-3">Toggle which options appear when users tap "Contact Support". The Email and Phone toggles above control the Email &amp; Call options; the toggle below controls the in-app Message (chat) option.</p>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-gray-200 text-sm font-medium">Message (Support Chat)</span>
                          <p className="text-gray-400 text-xs">Let users open a support chat ticket.</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setCompanyDetails({...companyDetails, show_support_chat: companyDetails.show_support_chat === false})}
                          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${companyDetails.show_support_chat !== false ? 'bg-blue-600' : 'bg-gray-600'}`}
                          data-testid="toggle-show-support-chat"
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${companyDetails.show_support_chat !== false ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-gray-300 text-sm font-medium mb-2">City</label>
                        <input
                          type="text"
                          value={companyDetails.city || ''}
                          onChange={(e) => setCompanyDetails({...companyDetails, city: e.target.value})}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-300 text-sm font-medium mb-2">Country</label>
                        <input
                          type="text"
                          value={companyDetails.country || ''}
                          onChange={(e) => setCompanyDetails({...companyDetails, country: e.target.value})}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-300 text-sm font-medium mb-2">Website</label>
                        <input
                          type="url"
                          value={companyDetails.website || ''}
                          onChange={(e) => setCompanyDetails({...companyDetails, website: e.target.value})}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-gray-300 text-sm font-medium mb-2">Privacy/Data Protection Email</label>
                      <p className="text-xs text-gray-400 mb-1">This email will appear in the Privacy Policy and data request pages.</p>
                      <input
                        type="email"
                        value={companyDetails.privacy_email || ''}
                        onChange={(e) => setCompanyDetails({...companyDetails, privacy_email: e.target.value})}
                        placeholder="e.g. privacy@goodjob.ph"
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid="privacy-email-input"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-300 text-sm font-medium mb-2">SEC/DTI Registration #</label>
                        <input
                          type="text"
                          value={companyDetails.registration_number || ''}
                          onChange={(e) => setCompanyDetails({...companyDetails, registration_number: e.target.value})}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-300 text-sm font-medium mb-2">Tax ID (TIN)</label>
                        <input
                          type="text"
                          value={companyDetails.tax_id || ''}
                          onChange={(e) => setCompanyDetails({...companyDetails, tax_id: e.target.value})}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <h3 className="text-lg font-bold text-white mt-6 mb-4">Social Media</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-300 text-sm font-medium mb-2">Facebook URL</label>
                        <input
                          type="url"
                          value={companyDetails.social_facebook || ''}
                          onChange={(e) => setCompanyDetails({...companyDetails, social_facebook: e.target.value})}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-300 text-sm font-medium mb-2">Instagram URL</label>
                        <input
                          type="url"
                          value={companyDetails.social_instagram || ''}
                          onChange={(e) => setCompanyDetails({...companyDetails, social_instagram: e.target.value})}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-300 text-sm font-medium mb-2">Twitter URL</label>
                        <input
                          type="url"
                          value={companyDetails.social_twitter || ''}
                          onChange={(e) => setCompanyDetails({...companyDetails, social_twitter: e.target.value})}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-300 text-sm font-medium mb-2">LinkedIn URL</label>
                        <input
                          type="url"
                          value={companyDetails.social_linkedin || ''}
                          onChange={(e) => setCompanyDetails({...companyDetails, social_linkedin: e.target.value})}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <h3 className="text-lg font-bold text-white mt-6 mb-4">Footer Content Pages</h3>
                    <p className="text-gray-400 text-sm mb-4">These fields populate the footer links and their corresponding pages. Use HTML or plain text.</p>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-gray-300 text-sm font-medium mb-2">About Us</label>
                        <textarea
                          value={companyDetails.about_us || ''}
                          onChange={(e) => setCompanyDetails({...companyDetails, about_us: e.target.value})}
                          rows="6"
                          placeholder="Enter your company's About Us content..."
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-gray-300 text-sm font-medium mb-2">Privacy Policy</label>
                        <textarea
                          value={companyDetails.privacy_policy || ''}
                          onChange={(e) => setCompanyDetails({...companyDetails, privacy_policy: e.target.value})}
                          rows="6"
                          placeholder="Enter your Privacy Policy content..."
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-gray-300 text-sm font-medium mb-2">Terms of Service</label>
                        <textarea
                          value={companyDetails.terms_of_service || ''}
                          onChange={(e) => setCompanyDetails({...companyDetails, terms_of_service: e.target.value})}
                          rows="6"
                          placeholder="Enter your Terms of Service content..."
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-gray-300 text-sm font-medium mb-2">Success Stories (For Providers)</label>
                        <textarea
                          value={companyDetails.success_stories || ''}
                          onChange={(e) => setCompanyDetails({...companyDetails, success_stories: e.target.value})}
                          rows="6"
                          placeholder="Enter success stories content..."
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-gray-300 text-sm font-medium mb-2">Provider Agreement</label>
                        <textarea
                          value={companyDetails.resources || ''}
                          onChange={(e) => setCompanyDetails({...companyDetails, resources: e.target.value})}
                          rows="6"
                          placeholder="Enter resources content for providers..."
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                        />
                      </div>
                    </div>

                    <button
                      onClick={handleSaveCompanyDetails}
                      disabled={saving}
                      className="mt-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg hover:shadow-lg font-semibold disabled:opacity-50"
                      data-testid="save-company-details-btn"
                    >
                      {saving ? 'Saving...' : 'Save Company Details'}
                    </button>
                  </div>
                </div>
              )}

              {/* Configuration */}
              {activeTab === 'config' && (
                <div data-testid="config-section">
                  <h2 className="text-2xl font-bold text-white mb-6">Platform Configuration</h2>
                  
                  <div className="space-y-6">
                    {/* Redis (Cache & Real-time Scaling) */}
                    <div className="bg-gray-700 rounded-lg p-4" data-testid="redis-config-card">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-lg font-bold text-white">⚡ Redis (Cache & Real-time Scaling)</h3>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${redisConfig.connected ? 'bg-green-500/20 text-green-300' : 'bg-gray-500/20 text-gray-300'}`}
                          data-testid="redis-status-badge"
                        >
                          {redisConfig.connected ? '● Connected' : '○ Not connected'}
                        </span>
                      </div>
                      <p className="text-gray-400 text-sm mb-3">
                        Optional. Powers multi-server real-time chat/notifications and config caching. Use a managed provider (e.g. Upstash) with a <code className="text-orange-300">rediss://</code> URL. Leave empty to run without Redis (single-server fallback).
                      </p>
                      <div className="text-xs text-gray-400 mb-3 space-y-1">
                        <div>Current: <span className="text-gray-200" data-testid="redis-current-url">{redisConfig.masked_url || 'Not configured'}</span></div>
                        <div>Source: <span className="text-gray-200">{redisConfig.source === 'database' ? 'Owner panel' : redisConfig.source === 'environment' ? 'Environment variable' : 'None'}</span>
                          {redisConfig.env_present && redisConfig.source === 'database' && <span className="text-yellow-400 ml-2">(overriding env var)</span>}
                        </div>
                      </div>
                      <label className="block text-gray-300 text-sm font-medium mb-2">Redis Connection URL</label>
                      <input
                        type="password"
                        value={redisInput}
                        onChange={(e) => setRedisInput(e.target.value)}
                        placeholder="rediss://default:password@host:6379"
                        className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white mb-3"
                        data-testid="redis-url-input"
                        autoComplete="off"
                      />
                      {redisTestResult && (
                        <div className={`text-sm mb-3 ${redisTestResult.ok ? 'text-green-400' : 'text-red-400'}`} data-testid="redis-test-result">
                          {redisTestResult.ok ? '✓ ' : '✗ '}{redisTestResult.message}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-3">
                        <button
                          onClick={testRedisConnection}
                          disabled={redisTesting || !redisInput.trim()}
                          className="px-4 py-2 rounded-lg border border-gray-500 text-gray-200 hover:bg-gray-600 disabled:opacity-50"
                          data-testid="redis-test-btn"
                        >
                          {redisTesting ? 'Testing…' : 'Test Connection'}
                        </button>
                        <button
                          onClick={saveRedisConfig}
                          disabled={redisSaving || !redisInput.trim()}
                          className="px-4 py-2 rounded-lg bg-orange-500 text-white font-semibold hover:bg-orange-600 disabled:opacity-50"
                          data-testid="redis-save-btn"
                        >
                          {redisSaving ? 'Saving…' : 'Save & Connect'}
                        </button>
                        {redisConfig.source === 'database' && (
                          <button
                            onClick={clearRedisConfig}
                            disabled={redisSaving}
                            className="px-4 py-2 rounded-lg border border-red-500/50 text-red-300 hover:bg-red-500/10 disabled:opacity-50"
                            data-testid="redis-clear-btn"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Commission Settings */}
                    <div className="bg-gray-700 rounded-lg p-4">
                      <h3 className="text-lg font-bold text-white mb-4">💰 Commission Settings</h3>
                      <p className="text-gray-400 text-sm mb-4">
                        Configure the platform fees. Client Fee is added to the service amount at checkout. Provider Fee is deducted from the service amount when paying the provider.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-gray-300 text-sm font-medium mb-2">
                            Client Fee (%)
                            <span className="text-gray-500 text-xs ml-2">Added to service amount</span>
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="50"
                            step="0.5"
                            value={appConfig.client_fee_rate}
                            onChange={(e) => {
                              const clientFee = parseFloat(e.target.value) || 0;
                              const providerFee = appConfig.provider_fee_rate || 5;
                              setAppConfig({
                                ...appConfig, 
                                client_fee_rate: clientFee,
                                platform_commission: clientFee + providerFee
                              });
                            }}
                            className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white"
                            data-testid="client-fee-rate-input"
                          />
                          <p className="text-xs text-gray-400 mt-1">
                            Example: For ₱1,000 service, client pays ₱{(1000 * (1 + (appConfig.client_fee_rate || 10) / 100)).toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <label className="block text-gray-300 text-sm font-medium mb-2">
                            Provider Fee (%)
                            <span className="text-gray-500 text-xs ml-2">Deducted from service amount</span>
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="50"
                            step="0.5"
                            value={appConfig.provider_fee_rate}
                            onChange={(e) => {
                              const providerFee = parseFloat(e.target.value) || 0;
                              const clientFee = appConfig.client_fee_rate || 10;
                              setAppConfig({
                                ...appConfig, 
                                provider_fee_rate: providerFee,
                                provider_share: 100 - providerFee,
                                platform_commission: clientFee + providerFee
                              });
                            }}
                            className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white"
                            data-testid="provider-fee-rate-input"
                          />
                          <p className="text-xs text-gray-400 mt-1">
                            Example: For ₱1,000 service, provider receives ₱{(1000 * (1 - (appConfig.provider_fee_rate || 5) / 100)).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="mt-4 p-3 bg-gray-600 rounded-lg">
                        <p className="text-sm text-gray-300 mb-2">
                          <strong>Fee Breakdown for ₱1,000 service:</strong>
                        </p>
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          <div className="bg-blue-900/50 p-2 rounded">
                            <p className="text-blue-300">Client Pays</p>
                            <p className="text-white font-bold">₱{(1000 * (1 + (appConfig.client_fee_rate || 10) / 100)).toLocaleString()}</p>
                            <p className="text-xs text-gray-400">₱1,000 + {appConfig.client_fee_rate || 10}% fee</p>
                          </div>
                          <div className="bg-green-900/50 p-2 rounded">
                            <p className="text-green-300">Provider Gets</p>
                            <p className="text-white font-bold">₱{(1000 * (1 - (appConfig.provider_fee_rate || 5) / 100)).toLocaleString()}</p>
                            <p className="text-xs text-gray-400">₱1,000 - {appConfig.provider_fee_rate || 5}% fee</p>
                          </div>
                          <div className="bg-yellow-900/50 p-2 rounded">
                            <p className="text-yellow-300">Platform Revenue</p>
                            <p className="text-white font-bold">₱{((1000 * (appConfig.client_fee_rate || 10) / 100) + (1000 * (appConfig.provider_fee_rate || 5) / 100)).toLocaleString()}</p>
                            <p className="text-xs text-gray-400">₱{(1000 * (appConfig.client_fee_rate || 10) / 100).toLocaleString()} + ₱{(1000 * (appConfig.provider_fee_rate || 5) / 100).toLocaleString()}</p>
                          </div>
                        </div>
                        <p className="text-xs text-gray-400 mt-2 text-center">
                          Total Platform Rate: {((appConfig.client_fee_rate || 10) + (appConfig.provider_fee_rate || 5))}% of service amount
                        </p>
                      </div>


                      {/* Auto-Confirm (escrow auto-release) window */}
                      <div className="mt-4 p-4 bg-gray-700 rounded-lg border border-gray-600">
                        <label className="block text-sm font-semibold text-gray-200 mb-1">Auto-Confirm Window (hours)</label>
                        <p className="text-xs text-gray-400 mb-2">
                          If a client doesn't respond to a provider's completion request within this many hours (and there is no active dispute), the job is auto-confirmed and funds are released to the provider.
                        </p>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="1"
                            max="720"
                            step="1"
                            value={appConfig.auto_confirm_hours ?? 24}
                            onChange={(e) => setAppConfig({ ...appConfig, auto_confirm_hours: parseInt(e.target.value, 10) || 0 })}
                            className="w-32 px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-600 focus:border-orange-500 focus:outline-none"
                            data-testid="auto-confirm-hours-input"
                          />
                          <span className="text-sm text-gray-400">hours (≈ {(((appConfig.auto_confirm_hours ?? 24)) / 24).toFixed(1)} days)</span>
                        </div>
                      </div>

                      {/* Fee Allocation Breakdown (editable) */}
                      {(() => {
                        const allocation = appConfig.fee_allocation || [];
                        const allocTotal = allocation.reduce((s, it) => s + (parseFloat(it.value) || 0), 0);
                        const clientRate = parseFloat(appConfig.client_fee_rate) || 0;
                        const providerRate = parseFloat(appConfig.provider_fee_rate) || 0;
                        const target = clientRate;
                        const ratesEqual = Math.abs(clientRate - providerRate) < 0.001;
                        const matches = Math.abs(allocTotal - target) < 0.001;
                        const updateRow = (idx, field, val) => {
                          const next = allocation.map((it, i) => i === idx ? { ...it, [field]: field === 'value' ? val : val } : it);
                          setAppConfig({ ...appConfig, fee_allocation: next });
                        };
                        const removeRow = (idx) => setAppConfig({ ...appConfig, fee_allocation: allocation.filter((_, i) => i !== idx) });
                        const addRow = () => setAppConfig({ ...appConfig, fee_allocation: [...allocation, { label: '', value: 0 }] });
                        return (
                          <div className="mt-4 bg-gray-800 rounded-lg p-4" data-testid="fee-allocation-editor">
                            <h4 className="text-white font-bold mb-1">Fee Allocation Breakdown</h4>
                            <p className="text-gray-400 text-xs mb-3">
                              Shown to clients & providers under "Where this fee goes". These represent how your platform fee ({ratesEqual ? `${target}%` : `client ${clientRate}% · provider ${providerRate}%`}) is used, and are scaled to each side's fee on their card. For exact figures, make the items add up to your platform fee{ratesEqual ? ` (${target}%)` : ''}.
                            </p>
                            <div className="space-y-2">
                              {allocation.map((item, idx) => (
                                <div key={idx} className="flex items-center gap-2" data-testid={`fee-alloc-row-${idx}`}>
                                  <input
                                    type="text"
                                    value={item.label}
                                    onChange={(e) => updateRow(idx, 'label', e.target.value)}
                                    placeholder="Label (e.g. Tax)"
                                    className="flex-1 px-3 py-2 rounded bg-gray-700 text-white text-sm border border-gray-600 focus:border-orange-500 outline-none"
                                    data-testid={`fee-alloc-label-${idx}`}
                                  />
                                  <div className="flex items-center gap-1">
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.1"
                                      value={item.value}
                                      onChange={(e) => updateRow(idx, 'value', e.target.value === '' ? '' : parseFloat(e.target.value))}
                                      className="w-20 px-2 py-2 rounded bg-gray-700 text-white text-sm border border-gray-600 focus:border-orange-500 outline-none text-right"
                                      data-testid={`fee-alloc-value-${idx}`}
                                    />
                                    <span className="text-gray-400 text-sm">%</span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeRow(idx)}
                                    className="text-red-400 hover:text-red-300 px-2 py-1"
                                    title="Remove"
                                    data-testid={`fee-alloc-remove-${idx}`}
                                  >
                                    ✕
                                  </button>
                                </div>
                              ))}
                            </div>
                            <button
                              type="button"
                              onClick={addRow}
                              className="mt-3 text-sm text-orange-400 hover:text-orange-300 font-medium"
                              data-testid="fee-alloc-add-row"
                            >
                              + Add line item
                            </button>
                            <div
                              className={`mt-3 flex items-center justify-between rounded-lg px-3 py-2 text-sm font-semibold ${matches ? 'bg-green-900/40 text-green-300' : 'bg-amber-900/40 text-amber-300'}`}
                              data-testid="fee-alloc-total"
                            >
                              <span>Allocation total</span>
                              <span>{parseFloat(allocTotal.toFixed(2))}% / {target}%</span>
                            </div>
                            {!matches && (
                              <p className="text-amber-300 text-xs mt-1.5" data-testid="fee-alloc-warning">
                                ⚠ The line items add up to {parseFloat(allocTotal.toFixed(2))}%, which doesn't match your platform fee of {target}%. They'll still be scaled to each side's fee on the user's card, but for exact figures make them sum to {target}%.
                              </p>
                            )}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Feature Toggles */}
                    <div className="bg-gray-700 rounded-lg p-4">
                      <h3 className="text-lg font-bold text-white mb-4">🔧 Feature Toggles</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {[
                          { key: 'enable_registration', label: 'Enable Registration' },
                          { key: 'enable_bookings', label: 'Enable Bookings' },
                          { key: 'enable_messaging', label: 'Enable Messaging' },
                          { key: 'enable_reviews', label: 'Enable Reviews' },
                          { key: 'require_barangay_clearance', label: 'Require Barangay Clearance' },
                          { key: 'require_payment_method', label: 'Require Payment Method' }
                        ].map(toggle => (
                          <label key={toggle.key} className="flex items-center justify-between p-3 bg-gray-600 rounded-lg cursor-pointer">
                            <span className="text-gray-200">{toggle.label}</span>
                            <input
                              type="checkbox"
                              checked={appConfig[toggle.key]}
                              onChange={(e) => setAppConfig({...appConfig, [toggle.key]: e.target.checked})}
                              className="w-5 h-5"
                            />
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* KYC Verification Mode */}
                    <div className="bg-gray-700 rounded-lg p-4">
                      <h3 className="text-lg font-bold text-white mb-4">🔐 KYC Verification Mode</h3>
                      <p className="text-gray-400 text-sm mb-4">
                        Choose how user ID verification is handled during registration.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <label 
                          className={`flex items-center p-4 rounded-lg cursor-pointer border-2 transition-all ${
                            appConfig.kyc_verification_mode === 'ai' 
                              ? 'bg-blue-600/20 border-blue-500' 
                              : 'bg-gray-600 border-gray-500 hover:border-gray-400'
                          }`}
                        >
                          <input
                            type="radio"
                            name="kyc_mode"
                            value="ai"
                            checked={appConfig.kyc_verification_mode === 'ai'}
                            onChange={(e) => setAppConfig({...appConfig, kyc_verification_mode: e.target.value})}
                            className="w-5 h-5 mr-3"
                          />
                          <div>
                            <span className="text-white font-semibold">🤖 AI Verification</span>
                            <p className="text-gray-400 text-sm mt-1">
                              AI automatically verifies ID documents and selfies. Faster but may require manual review for edge cases.
                            </p>
                          </div>
                        </label>
                        <label 
                          className={`flex items-center p-4 rounded-lg cursor-pointer border-2 transition-all ${
                            appConfig.kyc_verification_mode === 'admin' 
                              ? 'bg-orange-600/20 border-orange-500' 
                              : 'bg-gray-600 border-gray-500 hover:border-gray-400'
                          }`}
                        >
                          <input
                            type="radio"
                            name="kyc_mode"
                            value="admin"
                            checked={appConfig.kyc_verification_mode === 'admin'}
                            onChange={(e) => setAppConfig({...appConfig, kyc_verification_mode: e.target.value})}
                            className="w-5 h-5 mr-3"
                          />
                          <div>
                            <span className="text-white font-semibold">👤 Admin Verification</span>
                            <p className="text-gray-400 text-sm mt-1">
                              Admin manually reviews and verifies all ID documents. More control but requires admin attention.
                            </p>
                          </div>
                        </label>
                      </div>
                      <p className="text-yellow-400 text-xs mt-3">
                        💡 Current mode: <strong>{appConfig.kyc_verification_mode === 'ai' ? 'AI Verification' : 'Admin Verification'}</strong>
                      </p>
                    </div>

                    {/* Limits */}
                    <div className="bg-gray-700 rounded-lg p-4">
                      <h3 className="text-lg font-bold text-white mb-4">📏 Limits</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-gray-300 text-sm font-medium mb-2">Max Services/Provider</label>
                          <input
                            type="number"
                            value={appConfig.max_services_per_provider}
                            onChange={(e) => setAppConfig({...appConfig, max_services_per_provider: parseInt(e.target.value)})}
                            className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-gray-300 text-sm font-medium mb-2">Max Images/Service</label>
                          <input
                            type="number"
                            value={appConfig.max_images_per_service}
                            onChange={(e) => setAppConfig({...appConfig, max_images_per_service: parseInt(e.target.value)})}
                            className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-gray-300 text-sm font-medium mb-2">Max File Size (MB)</label>
                          <input
                            type="number"
                            value={appConfig.max_file_upload_size_mb}
                            onChange={(e) => setAppConfig({...appConfig, max_file_upload_size_mb: parseInt(e.target.value)})}
                            className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Maintenance Mode */}
                    <div className="bg-gray-700 rounded-lg p-4">
                      <h3 className="text-lg font-bold text-white mb-4">🔧 Maintenance Mode</h3>
                      <label className="flex items-center mb-4 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={appConfig.maintenance_mode}
                          onChange={(e) => setAppConfig({...appConfig, maintenance_mode: e.target.checked})}
                          className="w-5 h-5 mr-3"
                        />
                        <span className="text-gray-200">Enable Maintenance Mode</span>
                      </label>
                      {appConfig.maintenance_mode && (
                        <div>
                          <label className="block text-gray-300 text-sm font-medium mb-2">Maintenance Message</label>
                          <textarea
                            value={appConfig.maintenance_message || ''}
                            onChange={(e) => setAppConfig({...appConfig, maintenance_message: e.target.value})}
                            rows="2"
                            className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white"
                            placeholder="We're currently performing scheduled maintenance..."
                          />
                        </div>
                      )}
                    </div>

                    <button
                      onClick={handleSaveAppConfig}
                      disabled={saving}
                      className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg hover:shadow-lg font-semibold disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : 'Save Configuration'}
                    </button>
                  </div>

                  {/* PayMongo Gateway Configuration */}
                  <div className="mt-8 bg-gray-700 rounded-xl p-6" data-testid="paymongo-config">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-white">PayMongo Payment Gateway</h3>
                        <p className="text-gray-400 text-sm mt-1">
                          {paymongoConfig.paymongo_secret_key && !['sk_test_placeholder', ''].includes(paymongoConfig.paymongo_secret_key)
                            ? 'Live mode — PayMongo API keys configured'
                            : 'Sandbox mode — Payments are simulated. Add your PayMongo keys to enable real payments.'}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        paymongoConfig.paymongo_secret_key && !['sk_test_placeholder', ''].includes(paymongoConfig.paymongo_secret_key)
                          ? 'bg-emerald-500/15 text-emerald-400' : 'bg-amber-500/15 text-amber-400'
                      }`}>
                        {paymongoConfig.paymongo_secret_key && !['sk_test_placeholder', ''].includes(paymongoConfig.paymongo_secret_key) ? 'Live' : 'Sandbox'}
                      </span>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="text-gray-300 text-sm mb-1 block">Secret Key</label>
                        <div className="relative">
                          <input
                            type={showPaymongoKeys ? 'text' : 'password'}
                            value={paymongoConfig.paymongo_secret_key}
                            onChange={(e) => setPaymongoConfig(prev => ({ ...prev, paymongo_secret_key: e.target.value }))}
                            placeholder="sk_test_... or sk_live_..."
                            className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-blue-500 outline-none pr-20"
                            data-testid="paymongo-secret-key"
                          />
                          <button
                            onClick={() => setShowPaymongoKeys(!showPaymongoKeys)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white text-xs px-2 py-1"
                          >
                            {showPaymongoKeys ? 'Hide' : 'Show'}
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="text-gray-300 text-sm mb-1 block">Public Key</label>
                        <input
                          type={showPaymongoKeys ? 'text' : 'password'}
                          value={paymongoConfig.paymongo_public_key}
                          onChange={(e) => setPaymongoConfig(prev => ({ ...prev, paymongo_public_key: e.target.value }))}
                          placeholder="pk_test_... or pk_live_..."
                          className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-blue-500 outline-none"
                          data-testid="paymongo-public-key"
                        />
                      </div>

                      <div>
                        <label className="text-gray-300 text-sm mb-1 block">Webhook Secret (optional)</label>
                        <input
                          type={showPaymongoKeys ? 'text' : 'password'}
                          value={paymongoConfig.paymongo_webhook_secret}
                          onChange={(e) => setPaymongoConfig(prev => ({ ...prev, paymongo_webhook_secret: e.target.value }))}
                          placeholder="whsec_..."
                          className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-blue-500 outline-none"
                          data-testid="paymongo-webhook-secret"
                        />
                      </div>

                      {/* ===== Automated Payouts / Disbursements ===== */}
                      <div className="mt-6 pt-6 border-t border-gray-600" data-testid="paymongo-payouts-section">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="text-white font-semibold">Automated Payouts (Disbursements)</h4>
                          <span className={`text-xs px-2 py-1 rounded-full ${paymongoConfig.payouts_enabled ? 'bg-green-500/20 text-green-400' : 'bg-gray-600 text-gray-300'}`} data-testid="payouts-status-badge">
                            {paymongoConfig.payouts_enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                        <p className="text-gray-400 text-sm mb-4">
                          Send provider payouts to GCash/Maya automatically via PayMongo's Batch Transfers (Disbursements) API.
                          Requires your PayMongo wallet activated with programmatic disbursements access.
                        </p>

                        <div className="mb-4">
                          <label className="block text-gray-300 text-sm mb-1">Source Wallet Account Number</label>
                          <input
                            type="text"
                            value={paymongoConfig.paymongo_source_account_number}
                            onChange={(e) => setPaymongoConfig(prev => ({ ...prev, paymongo_source_account_number: e.target.value }))}
                            placeholder="e.g. 617144227299"
                            className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-blue-500 outline-none"
                            data-testid="paymongo-source-account-number"
                          />
                          <p className="text-gray-500 text-xs mt-1">Your PayMongo wallet account number (the InstaPay/PESONet "Account number" shown in your wallet top-up details). Funds are debited from this wallet.</p>
                        </div>

                        <div className="mb-4">
                          <label className="block text-gray-300 text-sm mb-1">Source Account Name</label>
                          <input
                            type="text"
                            value={paymongoConfig.paymongo_source_account_name}
                            onChange={(e) => setPaymongoConfig(prev => ({ ...prev, paymongo_source_account_name: e.target.value }))}
                            placeholder="e.g. CHITO Paragas DBA GoodJob"
                            className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-blue-500 outline-none"
                            data-testid="paymongo-source-account-name"
                          />
                          <p className="text-gray-500 text-xs mt-1">Must match the account name registered on your PayMongo wallet exactly.</p>
                        </div>

                        <div className="bg-gray-800 rounded-lg p-4 flex items-center justify-between mb-3">
                          <div>
                            <p className="text-white font-medium">Enable Automated Payouts</p>
                            <p className="text-gray-400 text-sm">Disburse on admin withdrawal approval. Leave off to keep the manual "Mark as Paid" flow.</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setPaymongoConfig(prev => ({ ...prev, payouts_enabled: !prev.payouts_enabled }))}
                            className={`relative inline-flex h-8 w-14 shrink-0 items-center rounded-full transition-colors ${paymongoConfig.payouts_enabled ? 'bg-green-500' : 'bg-gray-500'}`}
                            data-testid="toggle-payouts-enabled"
                          >
                            <span className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${paymongoConfig.payouts_enabled ? 'translate-x-7' : 'translate-x-1'}`} />
                          </button>
                        </div>

                        <div className="bg-gray-800 rounded-lg p-4 flex items-center justify-between">
                          <div>
                            <p className="text-white font-medium">Auto-payout on Job Completion</p>
                            <p className="text-gray-400 text-sm">Automatically disburse the moment escrow is released (no admin step). Requires payouts enabled.</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setPaymongoConfig(prev => ({ ...prev, auto_payout_on_release: !prev.auto_payout_on_release }))}
                            className={`relative inline-flex h-8 w-14 shrink-0 items-center rounded-full transition-colors ${paymongoConfig.auto_payout_on_release ? 'bg-green-500' : 'bg-gray-500'}`}
                            data-testid="toggle-auto-payout"
                          >
                            <span className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${paymongoConfig.auto_payout_on_release ? 'translate-x-7' : 'translate-x-1'}`} />
                          </button>
                        </div>
                      </div>

                      <p className="text-gray-500 text-xs">
                        Get your API keys from <a href="https://dashboard.paymongo.com/developers" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">PayMongo Developer Dashboard</a>. 
                        Use test keys (sk_test_...) for testing or live keys (sk_live_...) for production.
                      </p>

                      <button
                        onClick={handleSavePaymongoConfig}
                        disabled={paymongoSaving}
                        className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg hover:shadow-lg font-semibold disabled:opacity-50"
                        data-testid="save-paymongo-config-btn"
                      >
                        {paymongoSaving ? 'Saving...' : 'Save PayMongo Configuration'}
                      </button>
                    </div>

                    {/* OTP Configuration */}
                    <OTPConfigSection />

                    {/* Email Notifications (Resend) */}
                    <EmailConfigSection />
                  </div>
                </div>
              )}

              {/* Payment Methods */}
              {activeTab === 'payments' && (
                <div data-testid="payments-section">
                  <h2 className="text-2xl font-bold text-white mb-2">Client Payment Methods (Checkout)</h2>
                  <p className="text-gray-400 mb-6">
                    Enable or disable payment methods that <strong>clients</strong> can use to pay at checkout. Only methods <strong>approved on your PayMongo account</strong> will actually appear on the PayMongo checkout page — keep unapproved methods off here to avoid an empty checkout. (QR Ph works across all PH banks &amp; e-wallets.)
                  </p>
                  
                  <div className="space-y-4">
                    {/* GCash */}
                    <div className="bg-gray-700 rounded-lg p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <img src={PAYMENT_ICONS.gcash} alt="GCash" className="w-12 h-12 object-contain rounded-lg" />
                        <div>
                          <h3 className="text-lg font-semibold text-white">GCash</h3>
                          <p className="text-gray-400 text-sm">Mobile wallet payment via GCash</p>
                        </div>
                      </div>
                      <button
                        onClick={() => togglePaymentMethod('gcash')}
                        className={`relative inline-flex h-8 w-14 shrink-0 items-center rounded-full transition-colors ${
                          paymentMethods.gcash ? 'bg-green-500' : 'bg-gray-500'
                        }`}
                        data-testid="toggle-gcash"
                      >
                        <span
                          className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                            paymentMethods.gcash ? 'translate-x-7' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    {/* QR Ph */}
                    <div className="bg-gray-700 rounded-lg p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center font-bold text-sm">
                          <span className="text-blue-700">QR</span><span className="text-red-600">Ph</span>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-white">QR Ph</h3>
                          <p className="text-gray-400 text-sm">BSP-mandated QR payment (works across all PH banks & e-wallets)</p>
                        </div>
                      </div>
                      <button
                        onClick={() => togglePaymentMethod('qrph')}
                        className={`relative inline-flex h-8 w-14 shrink-0 items-center rounded-full transition-colors ${
                          paymentMethods.qrph ? 'bg-green-500' : 'bg-gray-500'
                        }`}
                        data-testid="toggle-qrph"
                      >
                        <span
                          className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                            paymentMethods.qrph ? 'translate-x-7' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    {/* Maya */}
                    <div className="bg-gray-700 rounded-lg p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <img src={PAYMENT_ICONS.maya} alt="Maya" className="w-12 h-12 object-contain rounded-lg" />
                        <div>
                          <h3 className="text-lg font-semibold text-white">Maya</h3>
                          <p className="text-gray-400 text-sm">Mobile wallet payment via Maya (formerly PayMaya)</p>
                        </div>
                      </div>
                      <button
                        onClick={() => togglePaymentMethod('maya')}
                        className={`relative inline-flex h-8 w-14 shrink-0 items-center rounded-full transition-colors ${
                          paymentMethods.maya ? 'bg-green-500' : 'bg-gray-500'
                        }`}
                        data-testid="toggle-maya"
                      >
                        <span
                          className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                            paymentMethods.maya ? 'translate-x-7' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    {/* Credit/Debit Card */}
                    <div className="bg-gray-700 rounded-lg p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center text-2xl">
                          💳
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-white">Credit/Debit Card</h3>
                          <p className="text-gray-400 text-sm">Visa, Mastercard, and other card payments</p>
                        </div>
                      </div>
                      <button
                        onClick={() => togglePaymentMethod('card')}
                        className={`relative inline-flex h-8 w-14 shrink-0 items-center rounded-full transition-colors ${
                          paymentMethods.card ? 'bg-green-500' : 'bg-gray-500'
                        }`}
                        data-testid="toggle-card"
                      >
                        <span
                          className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                            paymentMethods.card ? 'translate-x-7' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    {/* Bank Transfer */}
                    <div className="bg-gray-700 rounded-lg p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <img src={PAYMENT_ICONS.bank_transfer} alt="Bank Transfer" className="w-12 h-12 object-contain rounded-lg" />
                        <div>
                          <h3 className="text-lg font-semibold text-white">Bank Transfer</h3>
                          <p className="text-gray-400 text-sm">Manual bank transfer with proof of payment upload</p>
                        </div>
                      </div>
                      <button
                        onClick={() => togglePaymentMethod('bank_transfer')}
                        className={`relative inline-flex h-8 w-14 shrink-0 items-center rounded-full transition-colors ${
                          paymentMethods.bank_transfer ? 'bg-green-500' : 'bg-gray-500'
                        }`}
                        data-testid="toggle-bank_transfer"
                      >
                        <span
                          className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                            paymentMethods.bank_transfer ? 'translate-x-7' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  {/* Status Summary */}
                  <div className="mt-6 p-4 bg-gray-700 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-300 mb-3">Active Payment Methods</h4>
                    <div className="flex flex-wrap gap-2">
                      {paymentMethods.gcash && (
                        <span className="px-3 py-1 bg-blue-600 text-white text-sm rounded-full">GCash</span>
                      )}
                      {paymentMethods.qrph && (
                        <span className="px-3 py-1 bg-red-600 text-white text-sm rounded-full">QR Ph</span>
                      )}
                      {paymentMethods.maya && (
                        <span className="px-3 py-1 bg-green-600 text-white text-sm rounded-full">Maya</span>
                      )}
                      {paymentMethods.card && (
                        <span className="px-3 py-1 bg-purple-600 text-white text-sm rounded-full">Credit/Debit Card</span>
                      )}
                      {paymentMethods.bank_transfer && (
                        <span className="px-3 py-1 bg-gray-600 text-white text-sm rounded-full">Bank Transfer</span>
                      )}
                      {!paymentMethods.gcash && !paymentMethods.qrph && !paymentMethods.maya && !paymentMethods.card && !paymentMethods.bank_transfer && (
                        <span className="text-red-400 text-sm">⚠️ No payment methods enabled! Clients won't be able to pay.</span>
                      )}
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="mt-6">
                    <button
                      onClick={handleSavePaymentMethods}
                      disabled={savingPaymentMethods}
                      className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg hover:shadow-lg font-semibold disabled:opacity-50"
                      data-testid="save-payment-methods"
                    >
                      {savingPaymentMethods ? 'Saving...' : 'Save Payment Methods'}
                    </button>
                  </div>

                  {/* ===== Provider Payout Methods ===== */}
                  <div className="mt-12 pt-8 border-t border-gray-700" data-testid="payout-methods-section">
                    <h2 className="text-2xl font-bold text-white mb-2">Provider Payout Methods</h2>
                    <p className="text-gray-400 mb-6">
                      Choose which methods <strong>providers</strong> can register to <strong>receive their earnings</strong>. These are independent of checkout — payouts are sent manually, so they do <strong>not</strong> require PayMongo approval. (You can keep GCash/Maya on here even if they're off for checkout.)
                    </p>
                    <div className="space-y-4">
                      {[
                        { key: 'gcash', name: 'GCash', desc: 'Disburse earnings to a provider GCash wallet', icon: PAYMENT_ICONS.gcash },
                        { key: 'maya', name: 'Maya', desc: 'Disburse earnings to a provider Maya wallet', icon: PAYMENT_ICONS.maya },
                        { key: 'bank_transfer', name: 'Bank Account', desc: 'Disburse earnings to a provider bank account', icon: PAYMENT_ICONS.bank_transfer },
                      ].map(m => (
                        <div key={m.key} className="bg-gray-700 rounded-lg p-4 flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <img src={m.icon} alt={m.name} className="w-12 h-12 object-contain rounded-lg" />
                            <div>
                              <h3 className="text-lg font-semibold text-white">{m.name}</h3>
                              <p className="text-gray-400 text-sm">{m.desc}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => togglePayoutMethod(m.key)}
                            className={`relative inline-flex h-8 w-14 shrink-0 items-center rounded-full transition-colors ${
                              payoutMethods[m.key] ? 'bg-green-500' : 'bg-gray-500'
                            }`}
                            data-testid={`toggle-payout-${m.key}`}
                          >
                            <span
                              className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                                payoutMethods[m.key] ? 'translate-x-7' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      ))}
                    </div>

                    <div className="mt-6 p-4 bg-gray-700 rounded-lg">
                      <h4 className="text-sm font-semibold text-gray-300 mb-3">Active Payout Methods</h4>
                      <div className="flex flex-wrap gap-2">
                        {payoutMethods.gcash && <span className="px-3 py-1 bg-blue-600 text-white text-sm rounded-full">GCash</span>}
                        {payoutMethods.maya && <span className="px-3 py-1 bg-green-600 text-white text-sm rounded-full">Maya</span>}
                        {payoutMethods.bank_transfer && <span className="px-3 py-1 bg-gray-600 text-white text-sm rounded-full">Bank Account</span>}
                        {!payoutMethods.gcash && !payoutMethods.maya && !payoutMethods.bank_transfer && (
                          <span className="text-red-400 text-sm">⚠️ No payout methods enabled! Providers won't be able to register a way to get paid.</span>
                        )}
                      </div>
                    </div>

                    <div className="mt-6">
                      <button
                        onClick={handleSavePayoutMethods}
                        disabled={savingPayoutMethods}
                        className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-lg hover:shadow-lg font-semibold disabled:opacity-50"
                        data-testid="save-payout-methods"
                      >
                        {savingPayoutMethods ? 'Saving...' : 'Save Payout Methods'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* User Management */}
              {activeTab === 'users' && (
                <div data-testid="users-section">
                  <h2 className="text-2xl font-bold text-white mb-6">User Management</h2>
                  {(() => {
                    const filteredUsers = allUsers;
                    return (
                  <>
                  {/* Search */}
                  <div className="relative mb-4 max-w-md">
                    <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                    </svg>
                    <input
                      type="text"
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      placeholder="Search by name, email, or phone…"
                      className="w-full pl-9 pr-9 py-2.5 bg-gray-700 text-white placeholder-gray-400 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      data-testid="user-search-input"
                    />
                    {userSearch && (
                      <button
                        onClick={() => setUserSearch('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                        data-testid="user-search-clear"
                        aria-label="Clear search"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                  <p className="text-gray-400 mb-4" data-testid="user-count">
                    {userSearch.trim()
                      ? `Showing ${allUsers.length} of ${userTotal} match${userTotal === 1 ? '' : 'es'}`
                      : `Showing ${allUsers.length} of ${userTotal} users`}
                    {userListLoading && ' • Loading…'}
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full whitespace-nowrap" data-testid="user-management-table">
                      <thead className="bg-gray-700">
                        <tr>
                          <th className="px-4 py-3 text-left text-gray-300">User</th>
                          <th className="px-4 py-3 text-left text-gray-300">Phone</th>
                          <th className="px-4 py-3 text-left text-gray-300">Role Tags</th>
                          <th className="px-4 py-3 text-left text-gray-300">Status</th>
                          <th className="px-4 py-3 text-left text-gray-300">Verified</th>
                          <th className="px-4 py-3 text-left text-gray-300">Joined</th>
                          <th className="px-4 py-3 text-left text-gray-300">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-700">
                        {filteredUsers.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="px-4 py-8 text-center text-gray-400" data-testid="user-search-empty">
                              {userListLoading ? 'Loading…' : userSearch.trim() ? `No users match "${userSearch}".` : 'No users found.'}
                            </td>
                          </tr>
                        ) : filteredUsers.map((u) => (
                          <tr key={u.id} className="hover:bg-gray-700">
                            <td className="px-4 py-3">
                              <div className="text-white font-medium">{u.name}</div>
                              <div className="text-gray-400 text-sm">{u.email}</div>
                              {u.is_owner && <span className="text-yellow-400 text-xs">👑 Owner</span>}
                            </td>
                            <td className="px-4 py-3 text-gray-300 text-sm">
                              {u.phone || '-'}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {(u.available_roles || u.role_tags || [u.role]).map((tag, idx) => (
                                  <span 
                                    key={`${tag}-${idx}`}
                                    className={`px-2 py-1 rounded text-xs font-semibold ${
                                      tag === 'admin' ? 'bg-red-600 text-white' :
                                      tag === 'provider' ? 'bg-blue-600 text-white' :
                                      'bg-purple-600 text-white'
                                    }`}
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded text-xs ${
                                u.status === 'active' ? 'bg-green-900 text-green-300' :
                                u.status === 'suspended' ? 'bg-yellow-900 text-yellow-300' :
                                u.status === 'scheduled_for_deletion' ? 'bg-orange-900 text-orange-300' :
                                'bg-red-900 text-red-300'
                              }`}>
                                {u.status === 'scheduled_for_deletion' ? 'Pending deletion' : (u.status || 'active')}
                              </span>
                              {u.status === 'scheduled_for_deletion' && u.deletion_purge_at && (
                                <div className="text-[10px] text-orange-300/70 mt-1" data-testid={`purge-date-${u.id}`}>
                                  Purges {new Date(u.deletion_purge_at).toLocaleDateString()}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {u.id_verified ? (
                                <span className="text-green-400">✓ Verified</span>
                              ) : (
                                <span className="text-yellow-400">Pending</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-gray-400 text-sm">
                              {new Date(u.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-2">
                                {u.status === 'scheduled_for_deletion' && (
                                  <button
                                    onClick={() => handleRestoreAccount(u.id, u.name)}
                                    className="text-green-400 hover:text-green-300 text-sm font-semibold"
                                    data-testid={`restore-user-${u.id}`}
                                  >
                                    ♻️ Restore
                                  </button>
                                )}
                                <button
                                  onClick={() => openMessageUserModal(u)}
                                  className="text-orange-400 hover:text-orange-300 text-sm"
                                  data-testid={`message-user-${u.id}`}
                                >
                                  ✉️ Message
                                </button>
                                <button
                                  onClick={() => openEditUserModal(u)}
                                  className="text-blue-400 hover:text-blue-300 text-sm"
                                  data-testid={`edit-user-${u.id}`}
                                >
                                  ✏️ Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteUser(u.id)}
                                  className="text-red-400 hover:text-red-300 text-sm"
                                  data-testid={`delete-user-${u.id}`}
                                >
                                  🗑️ Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {userHasMore && (
                    <div className="text-center mt-4">
                      <button
                        onClick={() => fetchUsers(false)}
                        disabled={loadingMoreUsers}
                        className="px-5 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg border border-gray-600 disabled:opacity-50"
                        data-testid="users-load-more"
                      >
                        {loadingMoreUsers ? 'Loading…' : 'Load More'}
                      </button>
                    </div>
                  )}
                  </>
                  );
                  })()}
                </div>
              )}

              {/* Announcements */}
              {activeTab === 'announcements' && (
                <div data-testid="announcements-section">
                  <h2 className="text-2xl font-bold text-white mb-6">Announcements</h2>

                  {/* Create form */}
                  <div className="bg-gray-800 rounded-xl p-5 border border-gray-700 mb-8 max-w-2xl">
                    <h3 className="text-lg font-semibold text-white mb-4">Post a new announcement</h3>

                    <div className="flex items-center justify-between mb-4 bg-gray-700/50 rounded-lg px-3 py-2.5">
                      <div>
                        <span className="text-sm text-gray-200 font-medium">Show title & message</span>
                        <p className="text-xs text-gray-400">Turn off to show the image only.</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setAnnForm({ ...annForm, show_text: !annForm.show_text })}
                        className={`relative w-11 h-6 rounded-full transition shrink-0 ${annForm.show_text ? 'bg-orange-500' : 'bg-gray-600'}`}
                        data-testid="ann-showtext-toggle"
                      >
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${annForm.show_text ? 'translate-x-5' : ''}`} />
                      </button>
                    </div>

                    {annForm.show_text && (
                      <>
                        <label className="block text-sm text-gray-300 mb-1">Title</label>
                        <input
                          type="text"
                          value={annForm.title}
                          onChange={(e) => setAnnForm({ ...annForm, title: e.target.value })}
                          placeholder="Announcement title"
                          className="w-full mb-4 px-3 py-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500"
                          data-testid="ann-title-input"
                        />

                        <label className="block text-sm text-gray-300 mb-1">Message</label>
                        <textarea
                          value={annForm.body}
                          onChange={(e) => setAnnForm({ ...annForm, body: e.target.value })}
                          placeholder="Write your announcement..."
                          rows={4}
                          className="w-full mb-4 px-3 py-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500"
                          data-testid="ann-body-input"
                        />
                      </>
                    )}

                    <label className="block text-sm text-gray-300 mb-1">Image (optional)</label>
                    <div className="flex items-center gap-3 mb-4">
                      <label className="cursor-pointer bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg border border-gray-600 text-sm">
                        {annUploading ? 'Uploading…' : 'Upload Image'}
                        <input type="file" accept="image/*" className="hidden" onChange={handleAnnImageUpload} data-testid="ann-image-input" />
                      </label>
                      {annForm.image_url && (
                        <div className="flex items-center gap-2">
                          <img src={`${BACKEND_URL}${annForm.image_url}`} alt="preview" className="h-12 w-12 object-cover rounded" />
                          <button onClick={() => setAnnForm({ ...annForm, image_url: '' })} className="text-red-400 text-sm hover:underline">Remove</button>
                        </div>
                      )}
                    </div>

                    <label className="block text-sm text-gray-300 mb-2">Audience</label>
                    <div className="flex gap-6 mb-4">
                      <label className="flex items-center gap-2 text-gray-200 text-sm cursor-pointer">
                        <input type="checkbox" checked={annForm.target_clients} onChange={(e) => setAnnForm({ ...annForm, target_clients: e.target.checked })} className="w-4 h-4 accent-orange-500" data-testid="ann-target-clients" />
                        Clients
                      </label>
                      <label className="flex items-center gap-2 text-gray-200 text-sm cursor-pointer">
                        <input type="checkbox" checked={annForm.target_providers} onChange={(e) => setAnnForm({ ...annForm, target_providers: e.target.checked })} className="w-4 h-4 accent-orange-500" data-testid="ann-target-providers" />
                        Providers
                      </label>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm text-gray-300 mb-1">Start date (optional)</label>
                        <input type="date" value={annForm.start_date} onChange={(e) => setAnnForm({ ...annForm, start_date: e.target.value })} className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600" data-testid="ann-start-date" />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-300 mb-1">Expiry date</label>
                        <input type="date" value={annForm.expiry_date} onChange={(e) => setAnnForm({ ...annForm, expiry_date: e.target.value })} className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600" data-testid="ann-expiry-date" />
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-300">Add a link button</span>
                        <button
                          type="button"
                          onClick={() => setAnnForm({ ...annForm, link_enabled: !annForm.link_enabled })}
                          className={`relative w-11 h-6 rounded-full transition ${annForm.link_enabled ? 'bg-orange-500' : 'bg-gray-600'}`}
                          data-testid="ann-link-toggle"
                        >
                          <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${annForm.link_enabled ? 'translate-x-5' : ''}`} />
                        </button>
                      </label>
                      {annForm.link_enabled && (
                        <div className="grid grid-cols-2 gap-3">
                          <input type="text" value={annForm.link_url} onChange={(e) => setAnnForm({ ...annForm, link_url: e.target.value })} placeholder="https://... or /path" className="px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 text-sm" data-testid="ann-link-url" />
                          <input type="text" value={annForm.link_label} onChange={(e) => setAnnForm({ ...annForm, link_label: e.target.value })} placeholder="Button label" className="px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 text-sm" data-testid="ann-link-label" />
                        </div>
                      )}
                    </div>

                    <button
                      onClick={handleCreateAnnouncement}
                      disabled={annSubmitting}
                      className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-2.5 rounded-lg disabled:opacity-50"
                      data-testid="ann-submit"
                    >
                      {annSubmitting ? 'Posting…' : 'Post & Notify Users'}
                    </button>
                  </div>

                  {/* Existing announcements */}
                  <h3 className="text-lg font-semibold text-white mb-3">Posted announcements ({announcements.length})</h3>
                  <div className="space-y-3">
                    {announcements.length === 0 ? (
                      <p className="text-gray-400" data-testid="ann-empty">No announcements yet.</p>
                    ) : announcements.map((a) => (
                      <div key={a.id} className="bg-gray-800 rounded-lg p-4 border border-gray-700 flex items-start gap-4" data-testid={`ann-item-${a.id}`}>
                        {a.image_url && <img src={`${BACKEND_URL}${a.image_url}`} alt="" className="h-14 w-14 object-cover rounded shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-white font-semibold">{a.title}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${a.active ? 'bg-green-500/20 text-green-400' : 'bg-gray-600 text-gray-300'}`}>{a.active ? 'Active' : 'Inactive'}</span>
                            {a.target_clients && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300">Clients</span>}
                            {a.target_providers && <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300">Providers</span>}
                          </div>
                          {a.body && <p className="text-gray-400 text-sm mt-1 line-clamp-2">{a.body}</p>}
                          {a.expiry_date && <p className="text-gray-500 text-xs mt-1">Expires: {new Date(a.expiry_date).toLocaleDateString()}</p>}
                        </div>
                        <div className="flex flex-col gap-2 shrink-0">
                          <button onClick={() => toggleAnnouncementActive(a)} className="text-xs px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded" data-testid={`ann-toggle-${a.id}`}>
                            {a.active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button onClick={() => deleteAnnouncement(a.id)} className="text-xs px-3 py-1.5 bg-red-500/80 hover:bg-red-500 text-white rounded" data-testid={`ann-delete-${a.id}`}>
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Administrators */}
              {activeTab === 'admins' && (
                <div data-testid="admins-section">
                  <h2 className="text-2xl font-bold text-white mb-6">Administrator Management</h2>
                  
                  {/* Create Admin Form */}
                  <div className="bg-gray-700 rounded-lg p-4 mb-6">
                    <h3 className="text-lg font-bold text-white mb-4">➕ Create New Administrator</h3>
                    <form onSubmit={handleCreateAdmin}>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div>
                          <label className="block text-gray-300 text-sm mb-2">Name</label>
                          <input
                            type="text"
                            value={newAdmin.name}
                            onChange={(e) => setNewAdmin({...newAdmin, name: e.target.value})}
                            className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-gray-300 text-sm mb-2">Email</label>
                          <input
                            type="email"
                            value={newAdmin.email}
                            onChange={(e) => setNewAdmin({...newAdmin, email: e.target.value})}
                            className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-gray-300 text-sm mb-2">Password</label>
                          <input
                            type="password"
                            value={newAdmin.password}
                            onChange={(e) => setNewAdmin({...newAdmin, password: e.target.value})}
                            className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white"
                            required
                          />
                        </div>
                      </div>
                      <button
                        type="submit"
                        disabled={saving}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
                      >
                        {saving ? 'Creating...' : 'Create Admin'}
                      </button>
                    </form>
                  </div>

                  {/* Current Admins */}
                  <div className="bg-gray-700 rounded-lg p-4">
                    <h3 className="text-lg font-bold text-white mb-4">Current Administrators</h3>
                    <div className="space-y-3">
                      {adminsLoading && admins.length === 0 && (
                        <div className="text-gray-400 text-sm py-2" data-testid="admins-loading">Loading administrators…</div>
                      )}
                      {admins.map((admin) => (
                        <div key={admin.id} className="flex items-center justify-between p-3 bg-gray-600 rounded-lg">
                          <div>
                            <div className="text-white font-medium flex items-center gap-2">
                              {admin.name}
                              {admin.is_owner && <span className="text-yellow-400">👑 Owner</span>}
                            </div>
                            <div className="text-gray-400 text-sm">{admin.email}</div>
                          </div>
                          {!admin.is_owner && (
                            <div className="flex items-center gap-3">
                              {(myAllAccess || isOwner) && (
                                <button
                                  onClick={() => setPermAdmin(admin)}
                                  className="text-sky-300 hover:text-sky-200 text-sm font-semibold"
                                  data-testid={`admin-permissions-${admin.id}`}
                                >
                                  Permissions
                                </button>
                              )}
                              <button
                                onClick={() => handleDeleteUser(admin.id)}
                                className="text-red-400 hover:text-red-300"
                              >
                                Remove
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {permAdmin && (
                <AdminPermissionsModal
                  admin={permAdmin}
                  onClose={(saved) => { setPermAdmin(null); if (saved) fetchAdmins(); }}
                />
              )}

              {/* ID Verification (embedded) */}
              {activeTab === 'verification' && (
                <div data-testid="verification-section">
                  <AdminIDVerification user={user} onLogout={onLogout} embedded />
                </div>
              )}

              {/* Reports & Moderation */}
              {activeTab === 'moderation' && (
                <div data-testid="moderation-section">
                  <ModerationTab />
                </div>
              )}

              {/* Job Monitoring */}
              {activeTab === 'jobs' && (
                <div data-testid="jobs-section">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-white">Job Monitoring</h2>
                    <button
                      onClick={processExpiredJobs}
                      disabled={processingJobs}
                      className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2 rounded-lg hover:shadow-lg font-semibold disabled:opacity-50 flex items-center gap-2"
                      data-testid="process-expired-btn"
                    >
                      {processingJobs ? (
                        <>
                          <span className="animate-spin">⏳</span> Processing...
                        </>
                      ) : (
                        <>
                          🔄 Process Expired Jobs Now
                        </>
                      )}
                    </button>
                  </div>

                  {/* Stats Overview */}
                  {jobStats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="bg-gray-700 rounded-lg p-4">
                        <div className="text-3xl font-bold text-red-400">{jobStats.total_abandoned_jobs || 0}</div>
                        <div className="text-gray-400 text-sm">Total Abandoned Jobs</div>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-4">
                        <div className="text-3xl font-bold text-yellow-400">{jobStats.users_with_warnings || 0}</div>
                        <div className="text-gray-400 text-sm">Users with Warnings</div>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-4">
                        <div className="text-3xl font-bold text-purple-400">{jobStats.banned_users || 0}</div>
                        <div className="text-gray-400 text-sm">Banned Users</div>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-4">
                        <div className="text-3xl font-bold text-orange-400">{jobStats.recent_abandoned_30d || 0}</div>
                        <div className="text-gray-400 text-sm">Abandoned (Last 30 Days)</div>
                      </div>
                    </div>
                  )}

                  {/* Info Box */}
                  <div className="bg-blue-900/50 border border-blue-600 rounded-lg p-4 mb-6">
                    <h3 className="text-blue-200 font-semibold mb-2">ℹ️ How Job Abandonment Works</h3>
                    <ul className="text-blue-300 text-sm space-y-1">
                      <li>• Jobs are automatically marked as abandoned when the bidding deadline passes without a provider being selected</li>
                      <li>• Users receive a warning notification for each abandoned job</li>
                      <li>• After 3 abandoned jobs, the user's account is automatically banned</li>
                      <li>• Background task runs hourly to process expired jobs (or click the button above to process now)</li>
                    </ul>
                  </div>

                  {/* At-Risk Users */}
                  {jobStats?.at_risk_users && jobStats.at_risk_users.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-xl font-bold text-yellow-400 mb-4">⚠️ At-Risk Users (2 warnings)</h3>
                      <div className="bg-yellow-900/30 border border-yellow-600 rounded-lg overflow-hidden">
                        <table className="w-full">
                          <thead className="bg-yellow-900/50">
                            <tr>
                              <th className="text-left text-yellow-200 px-4 py-3 text-sm">User</th>
                              <th className="text-left text-yellow-200 px-4 py-3 text-sm">Email</th>
                              <th className="text-center text-yellow-200 px-4 py-3 text-sm">Abandoned Jobs</th>
                              <th className="text-center text-yellow-200 px-4 py-3 text-sm">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {jobStats.at_risk_users.map((user) => (
                              <tr key={user.id} className="border-t border-yellow-800">
                                <td className="px-4 py-3 text-white">{user.name}</td>
                                <td className="px-4 py-3 text-gray-300">{user.email}</td>
                                <td className="px-4 py-3 text-center">
                                  <span className="bg-yellow-600 text-white px-2 py-1 rounded-full text-sm">
                                    {user.abandoned_jobs_count}/3
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <button
                                    onClick={() => resetUserAbandonmentCount(user.id)}
                                    className="text-green-400 hover:text-green-300 text-sm"
                                    data-testid={`reset-abandonment-${user.id}`}
                                  >
                                    Reset Count
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Abandoned Jobs List */}
                  <div>
                    <h3 className="text-xl font-bold text-red-400 mb-4">🚫 Abandoned Jobs</h3>
                    {loadingJobs ? (
                      <div className="text-center py-8 text-gray-400">Loading abandoned jobs...</div>
                    ) : abandonedJobs.length === 0 ? (
                      <div className="text-center py-8 text-gray-400 bg-gray-700 rounded-lg">
                        <p className="text-4xl mb-2">✅</p>
                        <p>No abandoned jobs found. All jobs are in good standing!</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {abandonedJobs.map((job) => (
                          <div key={job.id} className="bg-gray-700 rounded-lg p-4 border-l-4 border-red-500">
                            <div className="flex justify-between items-start">
                              <div>
                                <h4 className="text-white font-semibold">{job.title}</h4>
                                <p className="text-gray-400 text-sm mt-1">{job.description?.substring(0, 100)}...</p>
                                <div className="flex flex-wrap gap-2 mt-2">
                                  <span className="bg-gray-600 text-gray-200 px-2 py-1 rounded text-xs">
                                    Category: {job.category}
                                  </span>
                                  <span className="bg-gray-600 text-gray-200 px-2 py-1 rounded text-xs">
                                    Budget: {job.currency || '₱'}{job.budget}
                                  </span>
                                  {job.total_bids > 0 && (
                                    <span className="bg-blue-600 text-white px-2 py-1 rounded text-xs">
                                      {job.total_bids} bid(s)
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="text-right text-sm">
                                <p className="text-red-400">Abandoned: {new Date(job.abandoned_at).toLocaleDateString()}</p>
                                {job.abandonment_reason && (
                                  <p className="text-gray-400 text-xs mt-1">{job.abandonment_reason}</p>
                                )}
                              </div>
                            </div>
                            {job.client && (
                              <div className="mt-3 pt-3 border-t border-gray-600">
                                <div className="flex items-center gap-3">
                                  <span className="text-gray-400 text-sm">Client:</span>
                                  <span className="text-white">{job.client.name}</span>
                                  <span className="text-gray-400">({job.client.email})</span>
                                  {job.client.abandoned_count > 0 && (
                                    <span className={`px-2 py-1 rounded text-xs ${
                                      job.client.abandoned_count >= 3 
                                        ? 'bg-red-600 text-white' 
                                        : job.client.abandoned_count >= 2 
                                          ? 'bg-yellow-600 text-white'
                                          : 'bg-gray-600 text-gray-200'
                                    }`}>
                                      {job.client.abandoned_count} warning(s)
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Ban Appeals Section */}
                  <div className="mt-8 pt-8 border-t border-gray-600">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl font-bold text-purple-400">📝 Ban Appeals</h3>
                      {appealStats && (
                        <div className="flex gap-3 text-sm">
                          <span className="bg-yellow-600 text-white px-3 py-1 rounded-full">
                            {appealStats.pending} Pending
                          </span>
                          <span className="bg-green-600 text-white px-3 py-1 rounded-full">
                            {appealStats.approved} Approved
                          </span>
                          <span className="bg-red-600 text-white px-3 py-1 rounded-full">
                            {appealStats.rejected} Rejected
                          </span>
                        </div>
                      )}
                    </div>

                    {loadingAppeals ? (
                      <div className="text-center py-8 text-gray-400">Loading appeals...</div>
                    ) : appeals.filter(a => a.status === 'pending').length === 0 ? (
                      <div className="text-center py-8 text-gray-400 bg-gray-700 rounded-lg">
                        <p className="text-4xl mb-2">📭</p>
                        <p>No pending appeals to review.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {appeals.filter(a => a.status === 'pending').map((appeal) => (
                          <div key={appeal.id} className="bg-gray-700 rounded-lg p-4 border-l-4 border-purple-500">
                            <div className="flex justify-between items-start mb-3">
                              <div>
                                <h4 className="text-white font-semibold">{appeal.user_name}</h4>
                                <p className="text-gray-400 text-sm">{appeal.user_email}</p>
                              </div>
                              <div className="text-right">
                                <span className="bg-yellow-600 text-white px-2 py-1 rounded text-xs">
                                  Pending Review
                                </span>
                                <p className="text-gray-400 text-xs mt-1">
                                  Submitted: {new Date(appeal.created_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>

                            <div className="mb-3 p-3 bg-gray-800 rounded-lg">
                              <p className="text-gray-400 text-xs mb-1">Original Ban Reason:</p>
                              <p className="text-red-400 text-sm">{appeal.ban_reason || 'N/A'}</p>
                            </div>

                            <div className="mb-4 p-3 bg-gray-800 rounded-lg">
                              <p className="text-gray-400 text-xs mb-1">User's Appeal:</p>
                              <p className="text-white text-sm">{appeal.reason}</p>
                            </div>

                            <div className="flex gap-3">
                              <button
                                onClick={() => handleApproveAppeal(appeal.id, 'Appeal approved. Account reinstated.')}
                                disabled={processingAppeal === appeal.id}
                                className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-500 font-medium disabled:opacity-50 transition"
                                data-testid={`approve-appeal-${appeal.id}`}
                              >
                                {processingAppeal === appeal.id ? 'Processing...' : '✓ Approve & Unban'}
                              </button>
                              <button
                                onClick={() => handleRejectAppeal(appeal.id, 'Appeal rejected. Ban upheld.')}
                                disabled={processingAppeal === appeal.id}
                                className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-500 font-medium disabled:opacity-50 transition"
                                data-testid={`reject-appeal-${appeal.id}`}
                              >
                                {processingAppeal === appeal.id ? 'Processing...' : '✗ Reject'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Recently Processed Appeals */}
                    {appeals.filter(a => a.status !== 'pending').length > 0 && (
                      <div className="mt-6">
                        <h4 className="text-gray-400 text-sm font-medium mb-3">Recently Processed Appeals</h4>
                        <div className="space-y-2">
                          {appeals.filter(a => a.status !== 'pending').slice(0, 5).map((appeal) => (
                            <div key={appeal.id} className="bg-gray-700/50 rounded-lg p-3 flex justify-between items-center">
                              <div>
                                <span className="text-white text-sm">{appeal.user_name}</span>
                                <span className="text-gray-400 text-sm ml-2">({appeal.user_email})</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className={`px-2 py-1 rounded text-xs ${
                                  appeal.status === 'approved' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
                                }`}>
                                  {appeal.status === 'approved' ? '✓ Approved' : '✗ Rejected'}
                                </span>
                                <span className="text-gray-500 text-xs">
                                  {appeal.reviewed_at ? new Date(appeal.reviewed_at).toLocaleDateString() : ''}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Disputes Tab */}
              {activeTab === 'disputes' && (
                <div data-testid="disputes-section">
                  <h2 className="text-2xl font-bold text-white mb-6">Dispute Resolution</h2>
                  
                  {/* Stats Cards */}
                  {disputeStats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="bg-gray-700 rounded-lg p-4">
                        <div className="text-3xl font-bold text-blue-400">{disputeStats.total_disputes}</div>
                        <div className="text-gray-400">Total Disputes</div>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-4">
                        <div className="text-3xl font-bold text-yellow-400">{disputeStats.open_disputes}</div>
                        <div className="text-gray-400">Open</div>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-4">
                        <div className="text-3xl font-bold text-red-400">{disputeStats.escalated_pending}</div>
                        <div className="text-gray-400">Escalated (Pending)</div>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-4">
                        <div className="text-3xl font-bold text-green-400">{disputeStats.resolved}</div>
                        <div className="text-gray-400">Resolved</div>
                      </div>
                    </div>
                  )}

                  {/* Penalty Stats */}
                  {disputeStats?.penalties && (
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="bg-orange-900/50 border border-orange-600 rounded-lg p-4">
                        <div className="text-2xl font-bold text-orange-400">{disputeStats.penalties.payment_deductions}</div>
                        <div className="text-orange-300 text-sm">Payment Deductions</div>
                      </div>
                      <div className="bg-yellow-900/50 border border-yellow-600 rounded-lg p-4">
                        <div className="text-2xl font-bold text-yellow-400">{disputeStats.penalties.suspensions}</div>
                        <div className="text-yellow-300 text-sm">Suspensions</div>
                      </div>
                      <div className="bg-red-900/50 border border-red-600 rounded-lg p-4">
                        <div className="text-2xl font-bold text-red-400">{disputeStats.penalties.deletions}</div>
                        <div className="text-red-300 text-sm">Account Deletions</div>
                      </div>
                    </div>
                  )}

                  <button
                    onClick={fetchDisputes}
                    disabled={loadingDisputes}
                    className="mb-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loadingDisputes ? 'Loading...' : '🔄 Refresh'}
                  </button>

                  {/* Disputes List */}
                  {loadingDisputes ? (
                    <div className="text-center py-8 text-gray-400">Loading disputes...</div>
                  ) : disputes.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">No disputes found</div>
                  ) : (
                    <div className="space-y-4">
                      {disputes.map((dispute) => (
                        <div
                          key={dispute.id}
                          className={`p-4 rounded-lg border ${
                            dispute.escalated_to_admin && dispute.status === 'escalated'
                              ? 'bg-red-900/30 border-red-600'
                              : dispute.status === 'resolved'
                              ? 'bg-green-900/30 border-green-600'
                              : 'bg-gray-700 border-gray-600'
                          }`}
                          data-testid={`dispute-${dispute.id}`}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h4 className="font-bold text-white">{dispute.service_title}</h4>
                                <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                  dispute.status === 'escalated' ? 'bg-red-600 text-white' :
                                  dispute.status === 'resolved' ? 'bg-green-600 text-white' :
                                  dispute.status === 'resubmitted' ? 'bg-purple-600 text-white' :
                                  'bg-yellow-600 text-white'
                                }`}>
                                  {dispute.status.replace('_', ' ').toUpperCase()}
                                </span>
                                {dispute.escalated_to_admin && dispute.status !== 'resolved' && (
                                  <span className="bg-red-600 text-white px-2 py-1 rounded text-xs font-semibold animate-pulse">
                                    ⚠️ NEEDS REVIEW
                                  </span>
                                )}
                              </div>
                              <div className="grid grid-cols-2 gap-4 text-sm text-gray-300 mb-3">
                                <div>
                                  <span className="text-gray-500">Client:</span> {dispute.client_name}
                                </div>
                                <div>
                                  <span className="text-gray-500">Provider:</span> {dispute.provider_name}
                                </div>
                                <div>
                                  <span className="text-gray-500">Amount:</span> ₱{dispute.disputed_amount?.toLocaleString()}
                                </div>
                                <div>
                                  <span className="text-gray-500">Resubmissions:</span> {dispute.resubmission_count || 0}/3
                                </div>
                              </div>
                              <div className="mb-2">
                                <span className="text-gray-500 text-sm">Reason:</span>
                                <p className="text-gray-300">{dispute.reason}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 text-sm">Description:</span>
                                <p className="text-gray-400 text-sm">{dispute.description}</p>
                              </div>
                              {/* Compact lifecycle progress */}
                              <div className="mt-3 max-w-md">
                                <DisputeProgressBadge dispute={dispute} dark />
                              </div>
                              {dispute.resolution_type && (
                                <div className="mt-3 p-3 bg-gray-800 rounded">
                                  <span className="text-green-400 font-semibold">Resolution: </span>
                                  <span className="text-gray-300">{dispute.resolution_type.replace('_', ' ')}</span>
                                  {dispute.penalty_percentage && (
                                    <span className="text-orange-400 ml-2">({dispute.penalty_percentage}% deduction)</span>
                                  )}
                                  {dispute.suspension_days && (
                                    <span className="text-yellow-400 ml-2">({dispute.suspension_days} days suspension)</span>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="flex flex-col gap-2 ml-4">
                              {dispute.status !== 'resolved' && dispute.status !== 'closed' && (
                                <button
                                  onClick={() => openResolveModal(dispute)}
                                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
                                  data-testid={`resolve-dispute-${dispute.id}`}
                                >
                                  Resolve
                                </button>
                              )}
                              <Link
                                to={`/disputes/${dispute.id}`}
                                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 text-sm text-center"
                              >
                                View Details
                              </Link>
                            </div>
                          </div>
                          <div className="mt-2 text-xs text-gray-500">
                            Opened: {new Date(dispute.created_at).toLocaleString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Activity Log */}
              {activeTab === 'activity' && (
                <div data-testid="activity-log-section">
                  <h2 className="text-2xl font-bold text-white mb-6">Admin Activity Log</h2>
                  <p className="text-gray-400 mb-6 text-sm">Track all modifications made by admin users across the platform.</p>

                  {/* Filters */}
                  <div className="bg-gray-800 rounded-lg p-4 mb-6" data-testid="activity-filters">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Admin User</label>
                        <select
                          value={activityFilter.admin_id}
                          onChange={(e) => setActivityFilter(prev => ({ ...prev, admin_id: e.target.value }))}
                          className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-sm text-gray-200"
                          data-testid="activity-filter-admin"
                        >
                          <option value="">All Admins</option>
                          {activityFilters.admins.map(a => (
                            <option key={a.id} value={a.id}>{a.name} ({a.email})</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Action Type</label>
                        <select
                          value={activityFilter.action}
                          onChange={(e) => setActivityFilter(prev => ({ ...prev, action: e.target.value }))}
                          className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-sm text-gray-200"
                          data-testid="activity-filter-action"
                        >
                          <option value="">All Actions</option>
                          {activityFilters.action_types.map(a => (
                            <option key={a} value={a}>{a.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">From</label>
                        <input
                          type="date"
                          value={activityFilter.start_date}
                          onChange={(e) => setActivityFilter(prev => ({ ...prev, start_date: e.target.value }))}
                          className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-sm text-gray-200"
                          data-testid="activity-filter-start"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">To</label>
                        <input
                          type="date"
                          value={activityFilter.end_date}
                          onChange={(e) => setActivityFilter(prev => ({ ...prev, end_date: e.target.value }))}
                          className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-sm text-gray-200"
                          data-testid="activity-filter-end"
                        />
                      </div>
                    </div>
                    {(activityFilter.admin_id || activityFilter.action || activityFilter.start_date || activityFilter.end_date) && (
                      <button
                        onClick={() => setActivityFilter({ admin_id: '', action: '', start_date: '', end_date: '' })}
                        className="mt-3 text-xs text-blue-400 hover:text-blue-300 underline"
                        data-testid="activity-clear-filters"
                      >
                        Clear all filters
                      </button>
                    )}
                  </div>

                  {/* Logs Table */}
                  {loadingActivity ? (
                    <div className="flex justify-center py-12">
                      <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : activityLogs.length === 0 ? (
                    <div className="bg-gray-800 rounded-lg p-12 text-center" data-testid="activity-empty">
                      <p className="text-gray-500 text-lg">No activity logs found</p>
                      <p className="text-gray-600 text-sm mt-1">Admin actions will appear here as they happen.</p>
                    </div>
                  ) : (
                    <div className="space-y-3" data-testid="activity-log-list">
                      {activityLogs.map((log) => (
                        <div key={log.id} className="bg-gray-800 border border-gray-700 rounded-lg p-4 hover:border-gray-600 transition" data-testid={`activity-log-${log.id}`}>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                                  log.action.includes('approved') || log.action.includes('created') ? 'bg-green-500/20 text-green-400' :
                                  log.action.includes('rejected') || log.action.includes('suspended') ? 'bg-red-500/20 text-red-400' :
                                  log.action.includes('updated') || log.action.includes('changed') ? 'bg-blue-500/20 text-blue-400' :
                                  log.action.includes('resolved') ? 'bg-purple-500/20 text-purple-400' :
                                  'bg-gray-600/30 text-gray-400'
                                }`} data-testid="activity-action-badge">
                                  {log.action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </span>
                                <span className="text-xs text-gray-500">{log.entity_type}</span>
                                {log.entity_type === 'user_critical_activity' && (
                                  <span className="px-2 py-0.5 rounded text-xs font-bold bg-red-600/30 text-red-300 border border-red-500/40 uppercase tracking-wide" data-testid="critical-activity-badge">
                                    ⚠ User Critical Activity
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-300">
                                <span className="font-medium text-white">{log.admin_name}</span>
                                <span className="text-gray-500"> ({log.admin_email})</span>
                              </p>
                              {log.entity_type === 'user_critical_activity' ? (
                                <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs bg-gray-900/60 border border-gray-700 rounded-lg p-3" data-testid="critical-activity-details">
                                  <div><span className="text-gray-500">Date:</span> <span className="text-gray-300">{new Date(log.created_at).toLocaleString()}</span></div>
                                  <div><span className="text-gray-500">User:</span> <span className="text-gray-300">{log.details?.target_name || '—'}</span></div>
                                  <div className="col-span-2"><span className="text-gray-500">Email:</span> <span className="text-gray-300">{log.details?.target_email || '—'}</span></div>
                                  <div className="col-span-2"><span className="text-gray-500">Reason:</span> <span className="text-gray-300">{log.details?.reason || '—'}</span></div>
                                  <div><span className="text-gray-500">Deleted By:</span> <span className="text-gray-300">{log.details?.deleted_by || '—'}</span></div>
                                  <div><span className="text-gray-500">OTP Verified:</span> <span className={log.details?.otp_verified ? 'text-green-400' : 'text-red-400'}>{log.details?.otp_verified ? 'Yes' : 'No'}{log.details?.otp_channel ? ` (${log.details.otp_channel})` : ''}</span></div>
                                  <div><span className="text-gray-500">IP Address:</span> <span className="text-gray-300">{log.details?.ip_address || '—'}</span></div>
                                  <div className="truncate" title={log.details?.device}><span className="text-gray-500">Device:</span> <span className="text-gray-300">{log.details?.device || '—'}</span></div>
                                  <div className="col-span-2">
                                    {log.action === 'account_restored'
                                      ? <><span className="text-gray-500">Outcome:</span> <span className="text-green-400">Restored by admin</span></>
                                      : <><span className="text-gray-500">Deleted Successfully:</span> <span className={log.details?.deleted_successfully ? 'text-green-400' : 'text-yellow-400'}>{log.details?.deleted_successfully ? 'Yes' : 'No'}</span></>}
                                  </div>
                                  {log.details?.purge_at && <div className="col-span-2"><span className="text-gray-500">Auto-purge:</span> <span className="text-gray-300">{new Date(log.details.purge_at).toLocaleString()}</span></div>}
                                </div>
                              ) : log.details && Object.keys(log.details).length > 0 && (
                                <div className="mt-2 text-xs text-gray-500 space-x-3">
                                  {log.details.target_name && <span>Target: <span className="text-gray-400">{log.details.target_name}</span></span>}
                                  {log.details.target_email && <span>Email: <span className="text-gray-400">{log.details.target_email}</span></span>}
                                  {log.details.amount && <span>Amount: <span className="text-gray-400">PHP {log.details.amount.toLocaleString()}</span></span>}
                                  {log.details.user_name && <span>User: <span className="text-gray-400">{log.details.user_name}</span></span>}
                                  {log.details.user_email && <span>Email: <span className="text-gray-400">{log.details.user_email}</span></span>}
                                  {log.details.fields_updated && <span>Fields: <span className="text-gray-400">{log.details.fields_updated.join(', ')}</span></span>}
                                  {log.details.fields_changed && <span>Changed: <span className="text-gray-400">{log.details.fields_changed.join(', ')}</span></span>}
                                  {log.details.reason && <span>Reason: <span className="text-gray-400">{log.details.reason}</span></span>}
                                  {log.details.resolution_type && <span>Resolution: <span className="text-gray-400">{log.details.resolution_type}</span></span>}
                                </div>
                              )}
                            </div>
                            <span className="text-xs text-gray-500 whitespace-nowrap">
                              {new Date(log.created_at).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Support Tickets */}
              {activeTab === 'support' && (
                <div data-testid="support-tickets-section">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-white">Support Tickets</h2>
                    <div className="flex gap-2">
                      {['all', 'open', 'closed'].map(f => (
                        <button
                          key={f}
                          onClick={() => setSupFilter(f)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${supFilter === f ? 'bg-orange-500 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                          data-testid={`sup-filter-${f}`}
                        >
                          {f.charAt(0).toUpperCase() + f.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Tickets list */}
                    <div className="lg:col-span-1 space-y-2 max-h-[600px] overflow-y-auto">
                      {supportTickets
                        .filter(t => supFilter === 'all' || t.status === supFilter)
                        .map(t => (
                        <button
                          key={t.id}
                          onClick={() => setActiveSupTicket(t)}
                          className={`w-full text-left p-3 rounded-lg transition border ${activeSupTicket?.id === t.id ? 'bg-orange-900/30 border-orange-500' : 'bg-gray-700 border-gray-600 hover:bg-gray-600'}`}
                          data-testid={`sup-ticket-${t.id}`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-white text-sm font-medium truncate flex-1 mr-2">{t.subject}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${t.status === 'open' ? 'bg-green-900 text-green-300' : 'bg-gray-600 text-gray-300'}`}>
                              {t.status}
                            </span>
                          </div>
                          <div className="text-xs text-gray-400">
                            {t.user_name} &middot; #{t.id.slice(0, 8).toUpperCase()}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">{new Date(t.created_at).toLocaleDateString()}</div>
                        </button>
                      ))}
                      {supportTickets.filter(t => supFilter === 'all' || t.status === supFilter).length === 0 && (
                        <div className="text-center py-8 text-gray-500 text-sm">No {supFilter !== 'all' ? supFilter : ''} tickets</div>
                      )}
                    </div>

                    {/* Chat area */}
                    <div className="lg:col-span-2 bg-gray-700 rounded-lg border border-gray-600 flex flex-col" style={{ height: '600px' }}>
                      {activeSupTicket ? (
                        <>
                          {/* Ticket header */}
                          <div className="px-4 py-3 border-b border-gray-600 flex items-center justify-between flex-shrink-0">
                            <div>
                              <div className="text-white font-semibold text-sm">{activeSupTicket.subject}</div>
                              <div className="text-xs text-gray-400">
                                #{activeSupTicket.id.slice(0, 8).toUpperCase()} &middot; {activeSupTicket.user_name} ({activeSupTicket.user_email})
                              </div>
                            </div>
                            <div className="flex gap-2">
                              {activeSupTicket.status === 'open' ? (
                                <button onClick={() => closeSupTicket(activeSupTicket.id)} className="bg-yellow-600 hover:bg-yellow-700 text-white text-xs px-3 py-1.5 rounded-lg transition" data-testid="sup-close-btn">
                                  Close
                                </button>
                              ) : (
                                <button onClick={() => reopenSupTicket(activeSupTicket.id)} className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1.5 rounded-lg transition" data-testid="sup-reopen-btn">
                                  Reopen
                                </button>
                              )}
                              <button onClick={() => deleteSupTicket(activeSupTicket.id)} className="bg-red-600 hover:bg-red-700 text-white text-xs px-3 py-1.5 rounded-lg transition" data-testid="sup-delete-btn">
                                Delete
                              </button>
                            </div>
                          </div>

                          {/* Messages */}
                          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                            {supMessages.map(msg => (
                              <div key={msg.id} className={`flex ${msg.sender_role === 'admin' ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[70%] px-3 py-2 rounded-2xl text-sm ${
                                  msg.sender_role === 'admin'
                                    ? 'bg-orange-500 text-white rounded-br-none'
                                    : 'bg-gray-600 text-gray-100 rounded-bl-none'
                                }`}>
                                  <div className="text-xs font-semibold mb-0.5 opacity-80">{msg.sender_name}</div>
                                  <div>{msg.message}</div>
                                  <div className="text-[10px] mt-1 opacity-60">
                                    {new Date(msg.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                  </div>
                                </div>
                              </div>
                            ))}
                            <div ref={supMsgEndRef} />
                          </div>

                          {/* Reply input */}
                          {activeSupTicket.status === 'open' ? (
                            <div className="px-4 py-3 border-t border-gray-600 flex gap-2 flex-shrink-0">
                              <input
                                type="text"
                                value={supReply}
                                onChange={e => setSupReply(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendSupReply(); } }}
                                placeholder="Reply as admin..."
                                className="flex-1 px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400"
                                data-testid="sup-reply-input"
                              />
                              <button
                                onClick={sendSupReply}
                                disabled={supSending || !supReply.trim()}
                                className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 transition"
                                data-testid="sup-reply-btn"
                              >
                                Send
                              </button>
                            </div>
                          ) : (
                            <div className="px-4 py-3 border-t border-gray-600 text-center text-sm text-gray-400 flex-shrink-0">
                              Ticket closed &middot; <button onClick={() => reopenSupTicket(activeSupTicket.id)} className="text-orange-400 hover:underline">Reopen</button>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
                          Select a ticket to view conversation
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Code Editor */}
              {activeTab === 'code' && (
                <div data-testid="code-section">
                  <h2 className="text-2xl font-bold text-white mb-6">Code Editor</h2>
                  
                  <div className="bg-yellow-900 border border-yellow-600 rounded-lg p-4 mb-6">
                    <p className="text-yellow-200 text-sm">
                      ⚠️ <strong>Warning:</strong> Modifying code files can break the application. 
                      Changes are backed up automatically. Core files (server.py, .env) cannot be modified for security.
                    </p>
                  </div>

                  {/* Directory Selection */}
                  <div className="flex gap-4 mb-4">
                    <button
                      onClick={() => fetchCodeFiles('backend')}
                      className={`px-4 py-2 rounded-lg ${selectedDirectory === 'backend' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'}`}
                    >
                      Backend (Python)
                    </button>
                    <button
                      onClick={() => fetchCodeFiles('frontend/src')}
                      className={`px-4 py-2 rounded-lg ${selectedDirectory === 'frontend/src' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'}`}
                    >
                      Frontend (React)
                    </button>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* File List */}
                    <div className="bg-gray-700 rounded-lg p-4 max-h-96 overflow-y-auto">
                      <h3 className="text-white font-bold mb-3">Files</h3>
                      {loadingFiles ? (
                        <p className="text-gray-400">Loading...</p>
                      ) : (
                        <div className="space-y-1">
                          {codeFiles.map((file) => (
                            <button
                              key={file.path}
                              onClick={() => loadFileContent(file.path)}
                              className={`w-full text-left px-3 py-2 rounded text-sm ${
                                selectedFile === file.path ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-600'
                              }`}
                            >
                              📄 {file.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Code Editor */}
                    <div className="lg:col-span-2 bg-gray-700 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="text-white font-bold">
                          {selectedFile ? `Editing: ${selectedFile}` : 'Select a file'}
                        </h3>
                        {selectedFile && (
                          <button
                            onClick={saveFileContent}
                            disabled={saving}
                            className="bg-green-600 text-white px-4 py-1 rounded text-sm hover:bg-green-700 disabled:opacity-50"
                          >
                            {saving ? 'Saving...' : 'Save'}
                          </button>
                        )}
                      </div>
                      <textarea
                        value={fileContent}
                        onChange={(e) => setFileContent(e.target.value)}
                        className="w-full h-80 px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-green-400 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Select a file to view/edit..."
                        disabled={!selectedFile}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Database Browser / Editor */}
              {activeTab === 'database' && (
                <DatabaseTab canEdit={canEdit('database')} />
              )}

              {/* Reset Data */}
              {activeTab === 'reset' && (
                <div data-testid="reset-section">
                  <h2 className="text-2xl font-bold text-white mb-6">Reset Platform Data</h2>

                  {/* ---------- Selective / Targeted Data Clear ---------- */}
                  <div className="bg-gray-800 border border-amber-600/50 rounded-lg p-6 mb-8" data-testid="selective-clear-section">
                    <div className="flex items-start gap-3 mb-4">
                      <span className="text-2xl">🧹</span>
                      <div>
                        <h3 className="text-amber-300 font-bold text-lg">Targeted Data Clear</h3>
                        <p className="text-gray-400 text-sm leading-relaxed">
                          Clear specific data categories (like transactions or messages) without deleting user accounts,
                          services, or jobs. Select only the categories you want to wipe.
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                      {clearCategories.map((cat) => (
                        <label
                          key={cat.key}
                          className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${
                            selectedClearKeys.includes(cat.key)
                              ? 'bg-amber-900/30 border-amber-500'
                              : 'bg-gray-700 border-gray-600 hover:border-gray-500'
                          }`}
                          data-testid={`clear-category-${cat.key}`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedClearKeys.includes(cat.key)}
                            onChange={() => toggleClearKey(cat.key)}
                            className="mt-1 accent-amber-500"
                            data-testid={`clear-checkbox-${cat.key}`}
                          />
                          <div>
                            <div className="text-white text-sm font-medium">{cat.label}</div>
                            <div className="text-gray-500 text-xs mt-0.5">{(cat.collections || []).join(', ')}</div>
                          </div>
                        </label>
                      ))}
                    </div>

                    <label
                      className="flex items-center gap-3 p-3 rounded-lg border border-gray-600 bg-gray-700 cursor-pointer mb-4"
                      data-testid="clear-reset-balances"
                    >
                      <input
                        type="checkbox"
                        checked={clearResetBalances}
                        onChange={(e) => setClearResetBalances(e.target.checked)}
                        className="accent-amber-500"
                        data-testid="clear-reset-balances-checkbox"
                      />
                      <div>
                        <div className="text-white text-sm font-medium">Also reset all user balances to ₱0</div>
                        <div className="text-gray-500 text-xs">Zeros out earnings, available balance, withdrawn & spent totals.</div>
                      </div>
                    </label>

                    {selectiveResult && (
                      <div className="bg-green-900/30 border border-green-600 rounded-lg p-4 mb-4" data-testid="selective-result">
                        <h4 className="text-green-300 font-semibold mb-2">Selected Data Cleared</h4>
                        <div className="text-sm text-green-200 space-y-1">
                          {Object.keys(selectiveResult.deleted_counts || {}).length === 0 ? (
                            <div>No matching records found to delete.</div>
                          ) : (
                            Object.entries(selectiveResult.deleted_counts || {}).map(([key, val]) => (
                              <div key={key}>{key}: {val} records deleted</div>
                            ))
                          )}
                          {selectiveResult.reset_balances && <div>User balances reset to ₱0</div>}
                        </div>
                      </div>
                    )}

                    <button
                      onClick={() => { setShowSelectiveModal(true); setSelectivePassword(''); }}
                      disabled={selectedClearKeys.length === 0 && !clearResetBalances}
                      className="bg-amber-600 text-white px-6 py-3 rounded-lg hover:bg-amber-700 font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="selective-clear-btn"
                    >
                      Clear Selected Data
                    </button>
                  </div>

                  <div className="bg-red-900/30 border border-red-600 rounded-lg p-6 mb-6">
                    <h3 className="text-red-300 font-bold text-lg mb-2">Danger Zone</h3>
                    <p className="text-red-200 text-sm leading-relaxed">
                      This will permanently delete all platform data and cannot be undone. Use this to prepare for a fresh launch.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-gray-700 rounded-lg p-4">
                      <h4 className="text-white font-semibold mb-3">What will be DELETED:</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-red-300">
                        <div>- All users (except owner)</div>
                        <div>- All bookings & payments</div>
                        <div>- All services & jobs</div>
                        <div>- All messages & inquiries</div>
                        <div>- All reviews & ratings</div>
                        <div>- All withdrawals & earnings</div>
                        <div>- All notifications</div>
                        <div>- All KYC & verification data</div>
                        <div>- All disputes & appeals</div>
                        <div>- All activity logs</div>
                      </div>
                    </div>

                    <div className="bg-gray-700 rounded-lg p-4">
                      <h4 className="text-white font-semibold mb-3">What will be PRESERVED:</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-green-300">
                        <div>- Owner account ({user?.email})</div>
                        <div>- Company details & branding</div>
                        <div>- Platform configuration</div>
                        <div>- Service categories</div>
                      </div>
                    </div>

                    {resetResult && (
                      <div className="bg-green-900/30 border border-green-600 rounded-lg p-4">
                        <h4 className="text-green-300 font-semibold mb-2">Reset Complete</h4>
                        <div className="text-sm text-green-200 space-y-1">
                          {Object.entries(resetResult.deleted_counts || {}).map(([key, val]) => (
                            <div key={key}>{key}: {val} records deleted</div>
                          ))}
                        </div>
                      </div>
                    )}

                    <button
                      onClick={() => { setShowResetModal(true); setResetPassword(''); setResetConfirmText(''); }}
                      className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 font-semibold transition"
                      data-testid="reset-data-btn"
                    >
                      Reset All Data
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Reset Data Confirmation Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" data-testid="reset-modal">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border border-red-600">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-900 rounded-full flex items-center justify-center">
                <span className="text-2xl">🗑️</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Confirm Data Reset</h3>
                <p className="text-gray-400 text-sm">This action is irreversible</p>
              </div>
            </div>

            <div className="bg-red-900/50 border border-red-600 rounded-lg p-3 mb-4">
              <p className="text-red-200 text-sm">
                All users, bookings, services, messages, and financial data will be permanently deleted. Only the owner account and platform settings will remain.
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Type <strong className="text-red-400">RESET</strong> to confirm
              </label>
              <input
                type="text"
                value={resetConfirmText}
                onChange={(e) => setResetConfirmText(e.target.value)}
                placeholder="Type RESET..."
                autoComplete="off"
                name="reset_confirm_nofill"
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500"
                data-testid="reset-confirm-text"
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Enter your account password
              </label>
              <input
                type="password"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                placeholder="Enter password..."
                autoComplete="new-password"
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500"
                data-testid="reset-password-input"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => { setShowResetModal(false); setResetPassword(''); setResetConfirmText(''); }}
                className="flex-1 bg-gray-600 text-white py-3 rounded-lg hover:bg-gray-500 font-semibold"
                data-testid="reset-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (resetConfirmText !== 'RESET') return;
                  setResetting(true);
                  try {
                    const res = await axios.post(`${API}/owner/reset-data`, { password: resetPassword }, {
                      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                    });
                    setResetResult(res.data);
                    setShowResetModal(false);
                    setSuccess('All platform data has been reset successfully');
                    setResetPassword('');
                    setResetConfirmText('');
                  } catch (err) {
                    setError(err.response?.data?.detail || 'Failed to reset data');
                  } finally {
                    setResetting(false);
                  }
                }}
                disabled={resetting || resetConfirmText !== 'RESET' || !resetPassword}
                className="flex-1 bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 font-semibold disabled:opacity-50"
                data-testid="reset-confirm-btn"
              >
                {resetting ? 'Resetting...' : 'Reset All Data'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Selective Clear Confirmation Modal */}
      {showSelectiveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" data-testid="selective-modal">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border border-amber-600">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-amber-900 rounded-full flex items-center justify-center">
                <span className="text-2xl">🧹</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Confirm Targeted Clear</h3>
                <p className="text-gray-400 text-sm">This action cannot be undone</p>
              </div>
            </div>

            <div className="bg-amber-900/40 border border-amber-600 rounded-lg p-3 mb-4">
              <p className="text-amber-200 text-sm mb-2">The following will be permanently cleared:</p>
              <ul className="text-amber-100 text-sm list-disc list-inside space-y-0.5">
                {selectedClearKeys.map((key) => {
                  const cat = clearCategories.find((c) => c.key === key);
                  return <li key={key}>{cat ? cat.label : key}</li>;
                })}
                {clearResetBalances && <li>Reset all user balances to ₱0</li>}
              </ul>
              <p className="text-green-300 text-xs mt-2">User accounts are preserved (unless Jobs/Services are selected).</p>
            </div>

            <div className="mb-4">
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Enter your account password
              </label>
              <input
                type="password"
                value={selectivePassword}
                onChange={(e) => setSelectivePassword(e.target.value)}
                placeholder="Enter password..."
                autoComplete="new-password"
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500"
                data-testid="selective-password-input"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => { setShowSelectiveModal(false); setSelectivePassword(''); }}
                className="flex-1 bg-gray-600 text-white py-3 rounded-lg hover:bg-gray-500 font-semibold"
                data-testid="selective-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setSelectiveClearing(true);
                  try {
                    const res = await axios.post(`${API}/owner/clear-data`, {
                      password: selectivePassword,
                      categories: selectedClearKeys,
                      reset_balances: clearResetBalances,
                    }, {
                      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                    });
                    setSelectiveResult(res.data);
                    setShowSelectiveModal(false);
                    setSelectivePassword('');
                    setSelectedClearKeys([]);
                    setClearResetBalances(false);
                    setSuccess('Selected data cleared successfully');
                  } catch (err) {
                    setError(err.response?.data?.detail || 'Failed to clear data');
                  } finally {
                    setSelectiveClearing(false);
                  }
                }}
                disabled={selectiveClearing || !selectivePassword}
                className="flex-1 bg-amber-600 text-white py-3 rounded-lg hover:bg-amber-700 font-semibold disabled:opacity-50"
                data-testid="selective-confirm-btn"
              >
                {selectiveClearing ? 'Clearing...' : 'Clear Data'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Confirmation Modal for Code Editor */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" data-testid="password-modal">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border border-gray-600">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-yellow-900 rounded-full flex items-center justify-center">
                <span className="text-2xl">🔐</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Security Confirmation</h3>
                <p className="text-gray-400 text-sm">Verify your identity to save changes</p>
              </div>
            </div>
            
            <div className="bg-yellow-900/50 border border-yellow-600 rounded-lg p-3 mb-4">
              <p className="text-yellow-200 text-sm">
                ⚠️ You are about to modify: <strong className="text-white">{selectedFile}</strong>
              </p>
              <p className="text-yellow-300 text-xs mt-1">
                This action will be logged for security purposes.
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Enter your account password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setPasswordError('');
                }}
                onKeyDown={(e) => e.key === 'Enter' && confirmSaveFile()}
                placeholder="Enter password..."
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="password-input"
                autoFocus
              />
              {passwordError && (
                <p className="text-red-400 text-sm mt-2" data-testid="password-error">
                  ✗ {passwordError}
                </p>
              )}
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowPasswordModal(false);
                  setConfirmPassword('');
                  setPasswordError('');
                }}
                className="flex-1 bg-gray-600 text-white py-3 rounded-lg hover:bg-gray-500 font-semibold"
                data-testid="cancel-save-btn"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmSaveFile}
                disabled={saving || !confirmPassword}
                className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-lg hover:shadow-lg font-semibold disabled:opacity-50"
                data-testid="confirm-save-btn"
              >
                {saving ? 'Verifying...' : 'Confirm & Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {msgModalUser && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4" data-testid="message-user-modal" onClick={() => !msgSending && setMsgModalUser(null)}>
          <div className="bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md border border-gray-700" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
              <h3 className="text-lg font-semibold text-white">Message {msgModalUser.name || 'user'}</h3>
              <button onClick={() => setMsgModalUser(null)} className="text-gray-400 hover:text-white" data-testid="message-user-close">✕</button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-xs text-gray-400">Sends a support conversation to this user. They'll see it in Messages → Admin Support and can reply.</p>
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-1">Sender <span className="text-gray-500">(optional)</span></label>
                <input
                  type="text"
                  value={msgSender}
                  onChange={(e) => setMsgSender(e.target.value)}
                  placeholder="GoodJob Admin"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="message-user-sender"
                />
                <p className="text-[11px] text-gray-500 mt-1">Leave empty to send as "GoodJob Admin".</p>
              </div>
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-1">Subject <span className="text-gray-500">(optional)</span></label>
                <input
                  type="text"
                  value={msgSubject}
                  onChange={(e) => setMsgSubject(e.target.value)}
                  placeholder="Message from GoodJob Admin"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="message-user-subject"
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-1">Message</label>
                <textarea
                  value={msgBody}
                  onChange={(e) => setMsgBody(e.target.value)}
                  rows={4}
                  placeholder="Write your message..."
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                  data-testid="message-user-body"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-700">
              <button onClick={() => setMsgModalUser(null)} disabled={msgSending} className="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-200 text-sm disabled:opacity-50">Cancel</button>
              <button onClick={handleSendUserMessage} disabled={msgSending || !msgBody.trim()} className="px-5 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm disabled:opacity-50" data-testid="message-user-send">
                {msgSending ? 'Sending…' : 'Send Message'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditUserModal && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" data-testid="owner-edit-user-modal">
          <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto border border-gray-700">
            <h2 className="text-2xl font-bold text-white mb-6">Edit User: {editingUser.name}</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Name */}
              <div>
                <label className="block text-gray-300 text-sm font-semibold mb-2">Name</label>
                <input
                  type="text"
                  value={editingUser.name || ''}
                  onChange={(e) => setEditingUser({...editingUser, name: e.target.value})}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                  data-testid="owner-edit-name"
                />
              </div>
              
              {/* Email */}
              <div>
                <label className="block text-gray-300 text-sm font-semibold mb-2">Email</label>
                <input
                  type="email"
                  value={editingUser.email || ''}
                  onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                  data-testid="owner-edit-email"
                />
              </div>
              
              {/* Phone */}
              <div>
                <label className="block text-gray-300 text-sm font-semibold mb-2">Phone</label>
                <input
                  type="tel"
                  value={editingUser.phone || ''}
                  onChange={(e) => setEditingUser({...editingUser, phone: e.target.value})}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="09123456789"
                  data-testid="owner-edit-phone"
                />
              </div>
              
              {/* Status */}
              <div>
                <label className="block text-gray-300 text-sm font-semibold mb-2">Status</label>
                <select
                  value={editingUser.status || 'active'}
                  onChange={(e) => setEditingUser({...editingUser, status: e.target.value})}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                  data-testid="owner-edit-status"
                >
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="banned">Banned</option>
                </select>
              </div>
            </div>

            {/* Suspension / Ban details (reason, dates, internal notes) */}
            {(editingUser.status === 'suspended' || editingUser.status === 'banned') && (
              <div className="mt-4 p-4 bg-gray-900 rounded-lg border border-amber-700/50" data-testid="suspension-details">
                <p className="text-amber-300 text-sm font-semibold mb-3">
                  {editingUser.status === 'suspended' ? 'Suspension' : 'Ban'} details
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className={editingUser.status === 'banned' ? 'md:col-span-2' : ''}>
                    <label className="block text-gray-400 text-xs mb-1">Reason (shown to user)</label>
                    <input
                      type="text"
                      value={editingUser.mod_reason || ''}
                      onChange={(e) => setEditingUser({ ...editingUser, mod_reason: e.target.value })}
                      placeholder="e.g. Repeated policy violations"
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm"
                      data-testid="mod-reason-input"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-xs mb-1">Effective date</label>
                    <input
                      type="datetime-local"
                      value={editingUser.mod_effective || ''}
                      onChange={(e) => setEditingUser({ ...editingUser, mod_effective: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm"
                      data-testid="mod-effective-input"
                    />
                  </div>
                  {editingUser.status === 'suspended' && (
                    <div>
                      <label className="block text-gray-400 text-xs mb-1">Expiration date (blank = indefinite)</label>
                      <input
                        type="datetime-local"
                        value={editingUser.mod_expiration || ''}
                        onChange={(e) => setEditingUser({ ...editingUser, mod_expiration: e.target.value })}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm"
                        data-testid="mod-expiration-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">Temporary suspensions auto-lift and notify the user on expiry.</p>
                    </div>
                  )}
                  <div className="md:col-span-2">
                    <label className="block text-gray-400 text-xs mb-1">Internal admin notes (private — not shown to user)</label>
                    <textarea
                      rows={2}
                      value={editingUser.mod_notes || ''}
                      onChange={(e) => setEditingUser({ ...editingUser, mod_notes: e.target.value })}
                      placeholder="Context for other admins…"
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm resize-none"
                      data-testid="mod-notes-input"
                    />
                  </div>
                </div>
              </div>
            )}
            
            {/* Role Checkboxes - Full Width */}
            <div className="mt-4">
              <label className="block text-gray-300 text-sm font-semibold mb-3">
                Assigned Roles (select up to 3)
              </label>
              <div className="flex flex-wrap gap-4">
                {[
                  { value: 'client', label: 'Client', icon: '👤', desc: 'Can hire services' },
                  { value: 'provider', label: 'Service Provider', icon: '🛠️', desc: 'Can offer services' },
                  { value: 'admin', label: 'Administrator', icon: '⚙️', desc: 'Can manage platform' }
                ].map((roleOption) => (
                  <label 
                    key={roleOption.value}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border-2 transition ${
                      (editingUser.available_roles || []).includes(roleOption.value)
                        ? 'bg-blue-600 border-blue-400 text-white'
                        : 'bg-gray-700 border-gray-600 text-gray-300 hover:border-gray-500'
                    }`}
                    data-testid={`role-checkbox-${roleOption.value}`}
                  >
                    <input
                      type="checkbox"
                      checked={(editingUser.available_roles || []).includes(roleOption.value)}
                      onChange={() => handleRoleToggle(roleOption.value)}
                      className="w-5 h-5 rounded accent-blue-500"
                    />
                    <div>
                      <span className="font-semibold">{roleOption.icon} {roleOption.label}</span>
                      <p className="text-xs opacity-75">{roleOption.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Current roles: {(editingUser.available_roles || ['client']).join(', ')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {/* Currency */}
              <div>
                <label className="block text-gray-300 text-sm font-semibold mb-2">Currency</label>
                <select
                  value={editingUser.currency || 'PHP'}
                  onChange={(e) => setEditingUser({...editingUser, currency: e.target.value})}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                  data-testid="owner-edit-currency"
                >
                  <option value="PHP">PHP (₱)</option>
                  <option value="USD">USD ($)</option>
                </select>
              </div>
            </div>
            
            {/* Bio */}
            <div className="mt-4">
              <label className="block text-gray-300 text-sm font-semibold mb-2">Bio</label>
              <textarea
                value={editingUser.bio || ''}
                onChange={(e) => setEditingUser({...editingUser, bio: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="User bio/description"
                data-testid="owner-edit-bio"
              />
            </div>
            
            {/* New Password */}
            <div className="mt-4 p-4 bg-gray-700 rounded-lg border border-yellow-600">
              <label className="block text-yellow-400 text-sm font-semibold mb-2">
                🔐 Set New Password (Leave blank to keep current)
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-yellow-500"
                placeholder="Minimum 6 characters"
                minLength={6}
                data-testid="owner-edit-password"
              />
              {newPassword && newPassword.length < 6 && (
                <p className="text-red-400 text-sm mt-1">Password must be at least 6 characters</p>
              )}
            </div>
            
            {/* ID Verification */}
            <div className="mt-4 flex items-center gap-3">
              <input
                type="checkbox"
                id="id_verified"
                checked={editingUser.id_verified || false}
                onChange={(e) => setEditingUser({...editingUser, id_verified: e.target.checked})}
                className="w-5 h-5 bg-gray-700 border-gray-600 rounded text-blue-500 focus:ring-blue-500"
                data-testid="owner-edit-verified"
              />
              <label htmlFor="id_verified" className="text-gray-300">
                ID Verified ✓
              </label>
            </div>

            {/* KYC Documents */}
            {(editingUser.id_front_image || editingUser.id_back_image || editingUser.selfie_with_id || editingUser.police_clearance) && (
              <div className="mt-4" data-testid="kyc-documents-section">
                <label className="block text-gray-300 text-sm font-semibold mb-3">Uploaded Documents</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { key: 'id_front_image', label: 'ID Front' },
                    { key: 'id_back_image', label: 'ID Back' },
                    { key: 'selfie_with_id', label: 'Selfie with ID' },
                    { key: 'police_clearance', label: 'Barangay Clearance' },
                  ].map(({ key, label }) => (
                    <div key={key} className="text-center">
                      <p className="text-xs text-gray-400 mb-1">{label}</p>
                      {editingUser[key] ? (
                        <a
                          href={`${BACKEND_URL}${editingUser[key]}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                          data-testid={`kyc-doc-${key}`}
                        >
                          <img
                            src={`${BACKEND_URL}${editingUser[key]}`}
                            alt={label}
                            className="w-full h-20 object-cover rounded-lg border border-gray-600 hover:border-blue-400 transition cursor-pointer"
                          />
                        </a>
                      ) : (
                        <div className="w-full h-20 bg-gray-700 rounded-lg border border-gray-600 flex items-center justify-center">
                          <span className="text-gray-500 text-xs">Not uploaded</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* User Info */}
            <div className="mt-4 p-3 bg-gray-700 rounded-lg text-sm text-gray-400">
              <p><strong>User ID:</strong> {editingUser.id}</p>
              <p><strong>Joined:</strong> {new Date(editingUser.created_at).toLocaleString()}</p>
              {editingUser.available_roles && (
                <p><strong>Available Roles:</strong> {editingUser.available_roles.join(', ')}</p>
              )}
              {editingUser.is_owner && (
                <p className="text-yellow-400"><strong>👑 Platform Owner</strong></p>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowEditUserModal(false);
                  setEditingUser(null);
                  setNewPassword('');
                }}
                className="flex-1 bg-gray-600 text-white py-3 rounded-lg hover:bg-gray-500 font-semibold"
                data-testid="owner-cancel-edit"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateUser}
                disabled={savingUser || (newPassword && newPassword.length < 6)}
                className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 rounded-lg hover:shadow-lg font-semibold disabled:opacity-50"
                data-testid="owner-save-edit"
              >
                {savingUser ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dispute Resolution Modal */}
      {showResolveModal && selectedDispute && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-4">Resolve Dispute</h3>
            
            <div className="mb-4 p-3 bg-gray-700 rounded-lg">
              <p className="text-gray-300 text-sm"><strong>Service:</strong> {selectedDispute.service_title}</p>
              <p className="text-gray-300 text-sm"><strong>Provider:</strong> {selectedDispute.provider_name}</p>
              <p className="text-gray-300 text-sm"><strong>Client:</strong> {selectedDispute.client_name}</p>
              <p className="text-gray-300 text-sm"><strong>Amount:</strong> ₱{selectedDispute.disputed_amount?.toLocaleString()}</p>
            </div>

            <div className="mb-4">
              <label className="block text-gray-300 mb-2">Resolution Type</label>
              <select
                value={resolutionType}
                onChange={(e) => setResolutionType(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-purple-500 focus:outline-none"
                data-testid="resolution-type-select"
              >
                <option value="">Select resolution...</option>
                <option value="cleared">✓ Cleared (No fault, release payment)</option>
                <option value="payment_deduction">💰 Payment Deduction (% penalty)</option>
                <option value="suspension">⏸️ Suspension (X days)</option>
                <option value="deletion">🗑️ Account Deletion (Permanent ban)</option>
              </select>
            </div>

            {resolutionType === 'payment_deduction' && (
              <div className="mb-4">
                <label className="block text-gray-300 mb-2">Penalty Percentage (%)</label>
                <input
                  type="number"
                  value={penaltyPercentage}
                  onChange={(e) => setPenaltyPercentage(e.target.value)}
                  min="1"
                  max="100"
                  placeholder="e.g., 20"
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-purple-500 focus:outline-none"
                  data-testid="penalty-percentage-input"
                />
                {penaltyPercentage && selectedDispute.disputed_amount && (
                  <p className="text-orange-400 text-sm mt-2">
                    Deduction: ₱{((selectedDispute.disputed_amount * penaltyPercentage) / 100).toLocaleString()} | 
                    Provider receives: ₱{(selectedDispute.disputed_amount - (selectedDispute.disputed_amount * penaltyPercentage) / 100).toLocaleString()}
                  </p>
                )}
              </div>
            )}

            {resolutionType === 'suspension' && (
              <div className="mb-4">
                <label className="block text-gray-300 mb-2">Suspension Days</label>
                <input
                  type="number"
                  value={suspensionDays}
                  onChange={(e) => setSuspensionDays(e.target.value)}
                  min="1"
                  placeholder="e.g., 7"
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-purple-500 focus:outline-none"
                  data-testid="suspension-days-input"
                />
                {suspensionDays && (
                  <p className="text-yellow-400 text-sm mt-2">
                    Provider will be automatically reinstated after {suspensionDays} days.
                  </p>
                )}
              </div>
            )}

            {(resolutionType === 'suspension' || resolutionType === 'payment_deduction') && (
              <div className="mb-4">
                <label className="flex items-center gap-2 text-gray-300">
                  <input
                    type="checkbox"
                    checked={refundToClient}
                    onChange={(e) => setRefundToClient(e.target.checked)}
                    className="w-4 h-4"
                  />
                  Refund client (deducted amount or full amount)
                </label>
              </div>
            )}

            {resolutionType === 'deletion' && (
              <div className="mb-4 p-3 bg-red-900/50 border border-red-600 rounded-lg">
                <p className="text-red-300 text-sm">
                  ⚠️ <strong>Warning:</strong> This will permanently ban the provider's account. 
                  Full refund will be issued to the client.
                </p>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-gray-300 mb-2">Resolution Notes</label>
              <textarea
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                rows="3"
                placeholder="Explain your decision..."
                className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-purple-500 focus:outline-none"
                data-testid="resolution-notes"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowResolveModal(false)}
                className="flex-1 bg-gray-600 text-white py-3 rounded-lg hover:bg-gray-500 font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={handleResolveDispute}
                disabled={processingResolution || !resolutionType || !resolutionNotes.trim()}
                className={`flex-1 py-3 rounded-lg font-semibold disabled:opacity-50 ${
                  resolutionType === 'deletion' 
                    ? 'bg-red-600 text-white hover:bg-red-700' 
                    : 'bg-purple-600 text-white hover:bg-purple-700'
                }`}
                data-testid="confirm-resolution-btn"
              >
                {processingResolution ? 'Processing...' : 'Confirm Resolution'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default OwnerPanel;
